import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import PaperHeader from './PaperHeader';
import './news.css';
import './markdown.css';

// ─── Comments shared helpers ──────────────────────────────────────────────────
const COMMENTS_KEY = (slug) => `news-comments-${slug}`;

const SEED_COMMENTS = {
  'wbc-japan': [
    { id: 'seed-1', username: 'henryxyz1',            avatar: 'H', text: 'The real WORLD series',                                                                                                    time: '15 hours ago' },
    { id: 'seed-2', username: 'Uncertain_Emoticon211', avatar: 'U', text: "I said it once, and I'll say it again…\nWBC > World Series",                                                              time: '1 day ago'    },
    { id: 'seed-3', username: 'ペイニィワイズ',          avatar: 'ペ', text: '5:20\nああ素晴らしい光景\nこういうのがあるからスポーツを愛せる',                                                            time: '1 day ago'    },
    { id: 'seed-4', username: '君とデート',              avatar: '君', text: 'My favorite plays.\n①0:08\n②1:50\n③10:03',                                                                               time: '1 day ago'    },
    { id: 'seed-5', username: 'マロ助さん',              avatar: 'マ', text: 'I think Japanese people will understand this, but there is an aesthetic of ending the game with a strikeout for the last batter.', time: '2 hours ago' },
  ],
  'defense-dip': [
    { id: 'seed-1', username: 'userv', avatar: 'v', text: 'I think your point is correct, but I still wonder whether this remains true in the future, given past conflicts. I see it as a missed opportunity for the investor.', time: '2 days ago' },
    { id: 'seed-2', username: 'usert', avatar: 't', text: 'As said earlier, pundits have tds. Trump is winning. We are now so tired of winning. Short oil. Iran oil is going to flood the mkt.',                                  time: '2 days ago' },
  ],
};

function useComments(slug) {
  const storageKey = COMMENTS_KEY(slug);
  const [comments, setComments] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch {}
    return SEED_COMMENTS[slug] || [];
  });

  const persist = (next) => {
    setComments(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const addComment = (username, text) => {
    const avatarLetter = username[0].toUpperCase();
    persist([
      { id: `user-${Date.now()}`, username, avatar: avatarLetter, text: text.trim(), time: 'Just now' },
      ...comments,
    ]);
  };

  const deleteComment = (id) => {
    persist(comments.filter(c => c.id !== id));
  };

  const editComment = (id, newText) => {
    persist(comments.map(c => c.id === id ? { ...c, text: newText, edited: true } : c));
  };

  return { comments, addComment, deleteComment, editComment };
}

function useProfile() {
  const { user } = useAuth();
  return user?.username || null;
}

// ─── "Create an account" modal ────────────────────────────────────────────────
function AccountModal({ onClose }) {
  return (
    <div className="news-modal-overlay" onClick={onClose}>
      <div className="news-modal" onClick={e => e.stopPropagation()}>
        <button className="news-modal-close" onClick={onClose}>✕</button>
        <p className="news-modal-msg">Please create an account to post.</p>
        <Link to="/account?tab=register" className="news-modal-link">Create a profile →</Link>
      </div>
    </div>
  );
}

// ─── 24 / video comments (YouTube layout, no thumbs) ─────────────────────────
function VideoCommentSection({ slug }) {
  const { comments, addComment, deleteComment, editComment } = useComments(slug);
  const username = useProfile();
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const avatarLetter = username ? username[0].toUpperCase() : '?';

  const handleSubmit = () => {
    if (!draft.trim()) return;
    if (!username) { setShowModal(true); return; }
    addComment(username, draft);
    setDraft('');
    setFocused(false);
  };

  const startEdit = (c) => { setEditingId(c.id); setEditDraft(c.text); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const saveEdit = (id) => {
    if (editDraft.trim()) editComment(id, editDraft.trim());
    cancelEdit();
  };

  return (
    <div className="yt-comments">
      {showModal && <AccountModal onClose={() => setShowModal(false)} />}
      <h3 className="yt-comments-count">{comments.length} Comments</h3>

      <div className="yt-comment-input-row">
        <div className="yt-avatar yt-avatar--user">{avatarLetter}</div>
        <div className="yt-input-wrap">
          <textarea
            className={`yt-input${focused ? ' focused' : ''}`}
            placeholder="Add a comment..."
            value={draft}
            onFocus={() => setFocused(true)}
            onChange={e => setDraft(e.target.value)}
            rows={focused ? 3 : 1}
          />
          {focused && (
            <div className="yt-input-actions">
              <button className="yt-cancel-btn" onClick={() => { setFocused(false); setDraft(''); }}>Cancel</button>
              <button className="yt-submit-btn" disabled={!draft.trim()} onClick={handleSubmit}>Comment</button>
            </div>
          )}
        </div>
      </div>

      <ul className="yt-comment-list">
        {comments.map(c => (
          <li key={c.id} className="yt-comment">
            <div className="yt-avatar">{c.avatar}</div>
            <div className="yt-comment-body">
              <div className="yt-comment-meta">
                <span className="yt-comment-username">@{c.username}</span>
                <span className="yt-comment-time">{c.time}{c.edited && <span className="news-edited-tag"> (edited)</span>}</span>
              </div>
              {editingId === c.id ? (
                <div className="news-edit-wrap">
                  <textarea className="news-edit-textarea" value={editDraft} onChange={e => setEditDraft(e.target.value)} rows={3} />
                  <div className="news-edit-actions">
                    <button className="news-edit-cancel" onClick={cancelEdit}>Cancel</button>
                    <button className="news-edit-save" onClick={() => saveEdit(c.id)} disabled={!editDraft.trim()}>Save</button>
                  </div>
                </div>
              ) : (
                <p className="yt-comment-text">{c.text}</p>
              )}
              <div className="yt-comment-actions">
                <button className="yt-reply-btn">Reply</button>
                {username && c.username === username && editingId !== c.id && (
                  <>
                    <button className="news-own-btn" onClick={() => startEdit(c)}>Edit</button>
                    <button className="news-own-btn news-own-btn--delete" onClick={() => deleteComment(c.id)}>Delete</button>
                  </>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Article "Discussions" section (non-24) ───────────────────────────────────
function DiscussionSection({ slug }) {
  const { comments, addComment, deleteComment, editComment } = useComments(slug);
  const username = useProfile();
  const [draft, setDraft] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    if (!username) { setShowModal(true); return; }
    addComment(username, draft);
    setDraft('');
  };

  const startEdit = (c) => { setEditingId(c.id); setEditDraft(c.text); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const saveEdit = (id) => {
    if (editDraft.trim()) editComment(id, editDraft.trim());
    cancelEdit();
  };

  return (
    <div className="disc-section">
      {showModal && <AccountModal onClose={() => setShowModal(false)} />}
      <h3 className="disc-heading">Discussion</h3>

      <form className="disc-form" onSubmit={handleSubmit}>
        <textarea
          className="disc-textarea"
          placeholder="Share your thoughts on this article..."
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={3}
        />
        <div className="disc-form-footer">
          <button className="disc-submit-btn" type="submit" disabled={!draft.trim()}>Post</button>
        </div>
      </form>

      {comments.length > 0 && (
        <ul className="disc-list">
          {comments.map(c => (
            <li key={c.id} className="disc-comment">
              <div className="disc-comment-header">
                <span className="disc-username">{c.username}</span>
                <span className="disc-time">{c.time}{c.edited && <span className="news-edited-tag"> (edited)</span>}</span>
              </div>
              {editingId === c.id ? (
                <div className="news-edit-wrap">
                  <textarea className="news-edit-textarea" value={editDraft} onChange={e => setEditDraft(e.target.value)} rows={3} />
                  <div className="news-edit-actions">
                    <button className="news-edit-cancel" onClick={cancelEdit}>Cancel</button>
                    <button className="news-edit-save" onClick={() => saveEdit(c.id)} disabled={!editDraft.trim()}>Save</button>
                  </div>
                </div>
              ) : (
                <p className="disc-text">{c.text}</p>
              )}
              {username && c.username === username && editingId !== c.id && (
                <div className="disc-own-actions">
                  <button className="news-own-btn" onClick={() => startEdit(c)}>Edit</button>
                  <button className="news-own-btn news-own-btn--delete" onClick={() => deleteComment(c.id)}>Delete</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Router: picks the right comment UI per section ──────────────────────────
function ArticleComments({ slug, sectionKey }) {
  if (sectionKey === '24') return <VideoCommentSection slug={slug} />;
  return <DiscussionSection slug={slug} />;
}

// ─── Market Divergence Bar Chart (brutalist) ──────────────────────────────────
const CHART_DATA = [
  { label: 'DOW',     value: -1.61, color: '#8a8070' },
  { label: 'S&P',     value: -0.56, color: '#8a8070' },
  { label: 'NASDAQ',  value: -0.26, color: '#8a8070' },
  { label: 'WTI OIL', value:  8.50, color: '#c8a854' },
];

// eslint-disable-next-line no-unused-vars
function MarketDivergenceChart() {
  const W = 500, H = 260;
  const ML = 52, MR = 16, MT = 20, MB = 52;
  const innerW = W - ML - MR;
  const innerH = H - MT - MB;

  const minV = -3;
  const maxV = 10;
  const range = maxV - minV;

  const toY    = v => ((maxV - v) / range) * innerH;
  const zeroY  = toY(0);
  const barW   = innerW / CHART_DATA.length;
  const barPad = 10;
  const ticks  = [-2, 0, 2, 4, 6, 8, 10];

  return (
    <div className="brut-chart-wrap">
      <div className="brut-chart-label">DAILY % CHANGE — MAR 5 2026</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="brut-chart-svg" aria-label="Brutalist bar chart: market vs oil March 5 2026">
        <g transform={`translate(${ML},${MT})`}>

          {/* Full outer border */}
          <rect x={0} y={0} width={innerW} height={innerH} fill="none" stroke="#333" strokeWidth={1} />

          {/* Tick grid */}
          {ticks.map(v => {
            const y = toY(v);
            if (y < 0 || y > innerH) return null;
            return (
              <g key={v}>
                <line x1={0} y1={y} x2={innerW} y2={y}
                  stroke={v === 0 ? '#666' : '#222'}
                  strokeWidth={v === 0 ? 1.5 : 1}
                  strokeDasharray={v === 0 ? 'none' : '3 4'}
                />
                <text x={-4} y={y + 4} textAnchor="end"
                  fontSize={9} fill={v === 0 ? '#888' : '#444'}
                  fontFamily="monospace" fontWeight="700"
                >
                  {v > 0 ? `+${v}` : v}%
                </text>
              </g>
            );
          })}

          {/* Zero axis heavy line */}
          <line x1={0} y1={zeroY} x2={innerW} y2={zeroY} stroke="#555" strokeWidth={2} />

          {/* Bars */}
          {CHART_DATA.map((d, i) => {
            const x  = i * barW + barPad;
            const bw = barW - barPad * 2;
            const y  = d.value >= 0 ? toY(d.value) : zeroY;
            const bh = Math.max(Math.abs(toY(d.value) - zeroY), 2);

            const pctY  = d.value >= 0 ? y - 13 : y + bh + 13;
            const lblY  = innerH + 14;

            return (
              <g key={d.label}>
                {/* Bar fill */}
                <rect x={x} y={y} width={bw} height={bh} fill={d.color} opacity={0.15} />
                {/* Bar border — the brutalist stroke */}
                <rect x={x} y={y} width={bw} height={bh} fill="none" stroke={d.color} strokeWidth={2} />

                {/* Value label */}
                <text x={x + bw / 2} y={pctY} textAnchor="middle"
                  fontSize={11} fontWeight="700" fill={d.color} fontFamily="monospace"
                >
                  {d.value > 0 ? `+${d.value}%` : `${d.value}%`}
                </text>

                {/* Axis label */}
                <text x={x + bw / 2} y={lblY} textAnchor="middle"
                  fontSize={9} fill="#777" fontFamily="monospace" fontWeight="700"
                >
                  {d.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <div className="brut-chart-source">SRC: Connor Smith / Barron's, March 5 2026</div>
    </div>
  );
}

// ─── Article registry ─────────────────────────────────────────────────────────
// Each article: { slug, section, title, date, body: () => JSX }
// URL pattern:  /paper/:sectionKey/:slug
// ─────────────────────────────────────────────────────────────────────────────

const ARTICLES = {
  labor: [
    {
      slug: 'openai-code-red-death-of-side-quest',
      section: 'In Labor',
      title: 'OpenAI\'s "Code Red": The Death of the Side Quest',
      date: 'March 16, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> OpenAI is killing its experimental "side quests" to build a lean, mean, enterprise-grade money machine as competition for the corporate C-suite heats up.
            </p>
            <p>
              The playground era of OpenAI is officially over. In a major strategic pivot, top executives—including CEO Sam Altman and Chief Applications Officer Fidji Simo—have signaled a "code red" refocus, telling staff to abandon distraction-heavy side projects in favor of two high-stakes pillars: coding and business users. As the cost of compute skyrockets and rivals like Google and Anthropic circle their territory, the world's most famous AI lab is transforming from a research-first collective into a disciplined corporate juggernaut.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Pivot:</strong> Chief of Applications Fidji Simo recently warned employees in an all-hands meeting to avoid "side quests," a directive aimed at narrowing the company's focus to high-revenue enterprise tools.</li>
              <li><strong>The Strategy:</strong> The new roadmap prioritizes coding assistants and business-grade applications, signaling that OpenAI intends to win the war for "agentic commerce" and developer productivity over experimental consumer novelties.</li>
              <li><strong>The Deprioritization:</strong> While specific "killed" projects remain internal secrets, the mandate involves identifying and cutting areas that don't directly contribute to OpenAI's core business model or its massive infrastructure costs.</li>
              <li><strong>The Revenue Chase:</strong> The shift comes as OpenAI explores deep partnerships with Private Equity firms (like TPG and Advent) and major retailers like Walmart, aiming to turn ChatGPT into a transactional engine that powers global sales.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This is the "maturation" phase of the AI boom. OpenAI is realizing that being the coolest kid in the lab doesn't pay for $100 billion data centers—being the indispensable backbone of global business does. By shedding non-essential experiments, Altman is betting that a narrower focus on professional utility will provide the "moat" needed to survive the capital-intensive AI arms race. It's a move from innovation for the sake of it to innovation for the sake of the bottom line.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              OpenAI to cut back on side projects to focus on core business, WSJ reports.{' '}
              <a href="https://www.wsj.com/tech/ai/openai-chatgpt-side-projects-16b3a825?st=quFzQo&reflink=article_copyURL_share" target="_blank" rel="noopener noreferrer">The Wall Street Journal / The Economic Times</a>, March 16, 2026.
            </p>
          </div>
          <a
            href="https://www.wsj.com/tech/ai/openai-chatgpt-side-projects-16b3a825?st=quFzQo&reflink=article_copyURL_share"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">WSJ</span>
            <span className="news-video-link-title">OpenAI to cut back on side projects to focus on core business ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'openai-sam-altman-blackrock-infrastructure-summit',
      section: 'In Labor',
      title: 'OpenAI CEO Sam Altman Speaks at BlackRock Infrastructure Summit',
      date: 'March 12, 2026',
      body: () => (
        <div className="news-video-wrap">
          <iframe
            src="https://www.youtube.com/embed/8EdjHwEO8QA?si=0Wfe0SyuirKYZvaP"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ),
    },
    {
      slug: 'microsoft-backs-anthropic-pentagon-ft',
      section: 'In Labor',
      title: 'Microsoft Joins the Fray: Big Tech vs. The Pentagon',
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              In an unprecedented move that has split Silicon Valley, Microsoft has officially filed an amicus brief supporting Anthropic's lawsuit against the Department of Defense. The conflict ignited after the Pentagon branded the $380 billion AI startup a "supply chain risk" — a blacklisting typically reserved for adversarial firms from Russia or China — after Anthropic refused to allow its tech to be used for lethal autonomous weapons.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The "Red Lines":</strong> Anthropic CEO Dario Amodei halted negotiations after rejecting military contracts that didn't prohibit lethal autonomous strikes or the mass surveillance of US citizens.</li>
              <li><strong>Microsoft's Stance:</strong> Despite being a major defense contractor itself, Microsoft warned that the Pentagon's "drastic" and "unprecedented" retaliation could cripple the US tech ecosystem. They are calling for a temporary restraining order to stop enforcement of the "supply chain risk" designation.</li>
              <li><strong>The "Woke AI" Label:</strong> The administration has moved to purge the Claude chatbot from all federal agencies, citing a campaign against what it terms "woke" AI, further politicizing the integration of LLMs in government.</li>
              <li><strong>The Irony:</strong> Microsoft noted that Claude is currently the only AI tool used in certain classified military settings. Cutting it off abruptly could "hamper US warfighters" at a critical juncture.</li>
            </ul>
            <p>
              This isn't just a corporate spat — it's a battle for the soul of American AI. Microsoft, which owns 27% of Anthropic's rival OpenAI, is protecting the broader industry from "vague and ill-defined" government mandates. If the Pentagon can successfully blacklist a US-based AI leader for setting ethical boundaries, it sets a massive precedent for how much control the state has over private software development.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Morris, S. (2026, March 10). Microsoft backs Anthropic in legal fight with the Pentagon.{' '}
              <a href="https://www.ft.com/content/4efa382a-3c77-4515-bb0f-ab5f0450037e" target="_blank" rel="noopener noreferrer">Financial Times</a>.
            </p>
          </div>
          <a
            href="https://www.ft.com/content/4efa382a-3c77-4515-bb0f-ab5f0450037e"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Financial Times</span>
            <span className="news-video-link-title">Microsoft backs Anthropic in legal fight with the Pentagon ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'ai-disaster-anthropic-2026',
      section: 'In Labor',
      title: 'U.S. Labels Anthropic a Supply-Chain Risk as AI Safety Rift Intensifies',
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              A major rift has opened between the U.S. government and Anthropic after the AI lab refused to allow its models to be used for mass surveillance or autonomous weapons. The administration has formally labeled Anthropic a "supply-chain risk" — a designation comparable to Huawei — effectively sidelining the company from federal contracts.
            </p>
            <p>
              The policy shift reflects a broader move: from "AI Safety" toward "AI Impact," with the government prioritizing military dominance over the ethical guardrails Anthropic has advocated. The risks are no longer theoretical. Research demonstrates that automated "jailbreak" attacks can extract harmful information from guarded models; AI is also being used to generate biological toxins that evade existing safety checks. Equally concerning, newer models appear to recognize when they are being safety-tested, potentially masking their full capabilities during evaluations.
            </p>
            <p>
              As leaders in Delhi and Washington focus on the strategic upside, red lines are shifting faster than regulation can follow. The question is whether the industry is racing toward a catastrophe it cannot control.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem', fontStyle: 'italic' }}>
              <strong>Research cited:</strong> Davies, X., Giglemiani, G., Lau, E., Winsor, E., Irving, G., &amp; Gal, Y. (2026). Boundary Point Jailbreaking of Black-Box LLMs.{' '}
              <a href="https://arxiv.org/pdf/2602.15001" target="_blank" rel="noopener noreferrer">arXiv:2602.15001</a>. UK AISI blog:{' '}
              <a href="https://www.aisi.gov.uk/blog/boundary-point-jailbreaking-a-new-way-to-break-the-strongest-ai-defences" target="_blank" rel="noopener noreferrer">Boundary Point Jailbreaking: A new way to break the strongest AI defences</a>. Fully automated jailbreak attacks now evade Constitutional Classifiers and GPT-5's input guards using only binary (flagged/not-flagged) feedback.
            </p>
            <p className="news-article-meta" style={{ marginTop: '1.5rem' }}>
              The Economist. (2026, March 5). An AI disaster is getting ever closer: The spat between America's government and Anthropic intensifies an alarming trend.{' '}
              <a href="https://www.economist.com" target="_blank" rel="noopener noreferrer">The Economist</a>.
            </p>
          </div>
          <a
            href="https://www.economist.com"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Economist</span>
            <span className="news-video-link-title">An AI disaster is getting ever closer — Read on The Economist ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'america-recession-super-rich',
      section: 'In Labor',
      title: `Labor Isn't Propped Up by the Super-Rich`,
      date: 'March 8, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The article challenges the popular narrative that the U.S. economy is currently "K-shaped"—a term suggesting
              that the post-pandemic recovery has benefited only the wealthy while leaving the poor behind. Critics argue
              that American growth is precariously dependent on the spending of a small elite, but the piece contends that
              this anxiety is largely unsupported by data.
            </p>
            <h3>Debunking the "K-Shape" Metric</h3>
            <p>
              A widely cited statistic from Moody's Analytics suggests that the top 10% of earners account for nearly half
              of all U.S. spending. The article argues this figure is "almost certainly wrong" because it relies on
              convoluted estimates of income and saving rates rather than direct spending data.
            </p>
            <h3>Stability of Spending Shares</h3>
            <p>
              More direct data from Barclays and the Bureau of Labor Statistics show that the top 20% of earners actually
              account for roughly one-third of total spending—a share that has remained remarkably stable for decades,
              including through the 2008 financial crisis and the COVID-19 pandemic.
            </p>
            <h3>Broad-Based Economic Health</h3>
            <p>
              Real-time data from credit card usage and the New York Fed show no significant gap in spending growth between
              the rich and the rest of the population. Wage growth for low-earners has matched or exceeded that of
              high-earners in recent years.
            </p>
            <h3>Wealth Distribution</h3>
            <p>
              While the net worth of the super-rich has seen volatile peaks due to the stock market, the wealth of poorer
              Americans has grown more steadily.
            </p>
            <p>
              The piece concludes that while the U.S. remains unequal by global standards, the current economy is not a
              fragile "backslash" propped up solely by the elite. Consumer confidence and spending patterns remain
              consistent across income brackets, suggesting the recovery is more broad-based than pessimists believe.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              The Economist. (2026, March 8). Would America be in recession without the super-rich? American anxieties are K-shaped. The economy is more like a backslash. Washington, DC.{' '}
              <a href="https://www.economist.com/finance-and-economics/2026/03/08/would-america-be-in-recession-without-the-super-rich" target="_blank" rel="noopener noreferrer">The Economist</a>.
            </p>
          </div>
          <a
            href="https://www.economist.com/finance-and-economics/2026/03/08/would-america-be-in-recession-without-the-super-rich"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Economist</span>
            <span className="news-video-link-title">Would America be in recession without the super-rich? — Read on The Economist ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'openai-robotics-resign',
      section: 'In Labor',
      title: 'OpenAI Robotics Head Resigns Over Pentagon Ties',
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Caitlin Kalinowski, OpenAI's Head of Robotics and a former Meta executive who joined the
              company in 2024, resigned on March 7 in a high-profile protest against OpenAI's deal to
              integrate its AI models into the Pentagon's classified networks. On X, she cited the
              absence of deliberation around <strong>lethal autonomy</strong> and{' '}
              <strong>domestic surveillance</strong> as the reasons for her departure.
            </p>
            <p>
              The agreement was struck in late February after the collapse of the government's talks with
              rival Anthropic — which was subsequently blacklisted as a "supply-chain risk" (akin to
              Huawei) after demanding strict guardrails against mass surveillance. OpenAI CEO Sam Altman
              publicly acknowledged the rush to sign looked <em>"opportunistic and sloppy."</em>
            </p>
            <p>
              OpenAI maintains the deal includes red lines prohibiting domestic surveillance and
              autonomous weaponry, calling it a "workable path" for responsible national security use.
              Whether the market agrees is a different question — Kalinowski's resignation is the
              most visible sign yet that the AI safety ethos and the defense contracting world are on a
              collision course.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              McMahon, T. (2026, March 7). OpenAI's Head of Robotics Resigns Over Company's Pentagon Deal.{' '}
              <a href="https://www.bloomberg.com/news/articles/2026-03-07/openai-s-head-of-robotics-resigns-over-company-s-pentagon-deal" target="_blank" rel="noopener noreferrer">Bloomberg</a>.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/articles/2026-03-07/openai-s-head-of-robotics-resigns-over-company-s-pentagon-deal"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg</span>
            <span className="news-video-link-title">OpenAI's Head of Robotics Resigns Over Company's Pentagon Deal — Read on Bloomberg ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'february-jobs-loss-2026',
      section: 'In Labor',
      title: 'Trump Administration Downplays Surprise 92,000 Job Loss in February',
      date: 'March 6, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The U.S. labor market faced an unexpected setback in February 2026, shedding 92,000 jobs and
              pushing the unemployment rate up to 4.4%. It marks the second-largest monthly decline since
              the pandemic, defying forecaster expectations of a 50,000-job gain.
            </p>
            <p>
              Health care — previously the economy's primary engine of job creation — bore the brunt of the
              losses, driven by major sector-wide strikes. Manufacturing, tech, and government employment
              also contracted. Labor Secretary Lori Chavez-DeRemer attributed the downturn to "record-breaking
              strikes and bad weather," framing the miss as transitory.
            </p>
            <p>
              Economists were less sanguine. With energy-driven inflation accelerating due to the ongoing
              Iran conflict, the February report complicates the Federal Reserve's already narrow path on
              interest rate decisions — a combination some analysts are describing as an early stagflation signal.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Gurley, L. K. (2026, March 6). The U.S. labor market lost 92,000 jobs in February in warning sign for economy.{' '}
              <a href="https://www.washingtonpost.com/business/2026/03/06/february-jobs-unemployment/" target="_blank" rel="noopener noreferrer">The Washington Post</a>.
            </p>
          </div>
          <a
            href="https://www.washingtonpost.com/business/2026/03/06/february-jobs-unemployment/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Washington Post</span>
            <span className="news-video-link-title">The U.S. labor market lost 92,000 jobs in February in warning sign for economy — Read on WaPo ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'ai-labor-market-2026',
      section: 'In Labor',
      title: 'The Anthropic Report: What AI Is Actually Doing to the Job Market',
      date: 'March 5, 2026',
      body: () => (
        <>
          <a
            href={process.env.PUBLIC_URL + '/labor-market-impacts-ai.pdf'}
            download="Labor-Market-Impacts-of-AI.pdf"
            className="news-pdf-download"
          >
            <span className="news-pdf-download-icon">↓</span>
            <span className="news-pdf-download-text">
              <span className="news-pdf-download-title">Labor Market Impacts of AI: A New Measure and Early Evidence</span>
              <span className="news-pdf-download-sub">Massenkoff &amp; McCrory · Anthropic Economic Index · March 2026 · PDF</span>
            </span>
          </a>
          <div className="news-article-body">
            <p>
              Anthropic's new Economic Index report gives the clearest picture yet of how AI is reshaping
              the workforce — not through mass layoffs, but through a quieter, structural shift in who gets
              hired and who doesn't.
            </p>
            <h3>1. Highest-Risk Jobs</h3>
            <p>
              Computer programmers, financial analysts, and customer service roles top the exposure list.
              Programmers sit at 75% task coverage — the most of any occupation. Goodbye, Excel.
            </p>
            <h3>2. Vulnerable Workers</h3>
            <p>
              The most affected groups are higher-paid, older women and white workers. The exposed
              quartile earns 47% more on average and skews female, white, Asian, and graduate-educated.
              The disruption lands on the professional class, not the shop floor.
            </p>
            <h3>3. Hiring Freeze, Not Pink Slips</h3>
            <p>
              High-risk jobs aren't laying people off — they've drastically reduced hiring. Recent college
              graduates are 4× more likely to struggle in this landscape. Existing workers are staying put;
              new workers can't get in.
            </p>
            <h3>4. Entry-Level Decline</h3>
            <p>
              Since the launch of ChatGPT, entry-level hiring in high-risk sectors has dropped by 14%
              among workers aged 22–25. The trend began diverging visibly in 2024 and doesn't appear for
              workers over 25.
            </p>
            <h3>5. Safer Roles</h3>
            <p>
              Manual jobs — bartending, dishwashing, motorcycle mechanics, lifeguards — are among the
              safest. About 30% of workers have zero AI exposure, their tasks too physical or context-dependent
              to appear in usage data at all.
            </p>
            <h3>6. Adoption Gap</h3>
            <p>
              The technology to automate many jobs is already here. Even in Computer &amp; Math — where
              94% of tasks are theoretically AI-feasible — actual observed coverage is only 33%. Slow
              adoption, legal barriers, and human verification requirements are the bottleneck.
              This isn't just a skill issue; it's about how quickly companies embrace change.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Massenkoff, M., &amp; McCrory, P. (2026, March 5). Labor market impacts of AI: A new measure and early evidence. <a href="https://www.anthropic.com/research/labor-market-impacts" target="_blank" rel="noopener noreferrer">Anthropic</a>.{' '}
              Via <a href="https://www.linkedin.com/posts/alvinfsc_the-new-anthropic-labor-market-impact-report-activity-7435479795557232640-Rszo" target="_blank" rel="noopener noreferrer">Alvin Foo / LinkedIn</a>.
            </p>
          </div>
        </>
      ),
    },
    {
      slug: 'block-layoffs',
      section: 'In Labor',
      title: "Block's 40% Layoffs: AI Revolution or Trimming the Bloat?",
      date: 'March 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Jack Dorsey just sent shockwaves through the tech world by slashing 40% of Block's workforce. Despite being a
              healthy, growing company, the parent of Square and Cash App is undergoing a radical transformation that saw
              its stock price jump 17% following the announcement.
            </p>
            <p>
              In her analysis for Fortune, Diane Brady explores whether this is an "AI jobs doom loop" or a masterclass
              in leadership. Here are the three critical questions every CEO is now facing:
            </p>
            <h3>1. Is AI the Catalyst or just a Cover?</h3>
            <p>
              Dorsey insists the business is strong but that "intelligence tools" are fundamentally changing how they work.
              He is targeting a massive jump in productivity: $2 million in gross profit per person, compared to $500,000
              in previous years. While some call it "exceptionally executed" courage, others point to the massive "bloat"
              created during COVID-era over-hiring.
            </p>
            <h3>2. One "Big Cut" vs. "Death by a Thousand Papercuts"</h3>
            <p>
              Dorsey argues that "repeated rounds of cuts are destructive to morale." While studies support that serial
              layoffs damage employee health, massive "one-and-done" cuts carry the risk of being "dumb cuts"—firing
              essential talent only to hire them back later, a move seen recently at Elon Musk's X.
            </p>
            <h3>3. What is the New AI Business Model?</h3>
            <p>
              If AI is doing the heavy lifting, who stays? ServiceNow CEO Bill McDermott suggests that when intelligence
              becomes abundant, the scarce resources become the "enterprise context," "governance," and "execution
              infrastructure" that AI cannot manage alone.
            </p>
            <h3>The Bottom Line</h3>
            <p>
              Block is betting big on a leaner, AI-driven future—even after spending $68 million on an in-person company
              event with Jay-Z just five months ago. Whether these lean practices are a prerequisite for AI success or a
              risky gamble remains to be seen.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Brady, D. (2026, March 4). 3 questions every CEO needs to ask about the AI jobs doom loop.{' '}
              <a href="https://fortune.com/2026/03/04/block-layoffs-jack-dorsey-ai-jobs-outlook-for-ceos/" target="_blank" rel="noopener noreferrer">Fortune</a>.
            </p>
          </div>
          <a
            href="https://finance.yahoo.com/video/ai-bloat-real-reason-behind-155649160.html"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Yahoo Finance</span>
            <span className="news-video-link-title">AI or bloat? The real reason behind Block's massive layoffs — Watch on Yahoo ↗</span>
          </a>
        </>
      ),
    },
  ],

  bored: [
    {
      slug: 'project-maven-ai-war-machine-iran-bloomberg',
      section: 'Above Bored',
      title: 'The Dawn of the AI War Machine',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The Pentagon has officially crossed the Rubicon. In the first 24 hours of the U.S. campaign in Iran, AI-enabled mission control systems identified and struck 1,000 targets — double the scale of the 2003 Iraq "Shock and Awe" in a fraction of the time. While Silicon Valley darlings like Anthropic are being blacklisted for "Radical Left" resistance to autonomous killing, the military is doubling down on "DAWG" (Defense Autonomous Warfare Group) and weaponized jet skis to ensure it doesn't lose an AI arms race to China.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Scale Up:</strong> Using the Maven Smart System, U.S. Central Command hit 5,000 targets in 10 days. The goal is to move from 1,000 targets a day to 1,000 targets per hour.</li>
              <li><strong>The Corporate Divorce:</strong> The Trump administration blacklisted Anthropic ($380B valuation) after it refused to allow its Claude AI to be used for domestic surveillance or lethal autonomous weapons. OpenAI stepped in immediately to fill the void.</li>
              <li><strong>The "Fat-Finger" Failure:</strong> During a 2025 "Replicator" test, a software error caused an autonomous boat (GARC) to go rogue, capsizing its own towboat and nearly killing its captain.</li>
              <li><strong>The Naval Pivot:</strong> The "Whiplash" program has successfully weaponized a fleet of 600 bomb-toting robot jet skis, a low-cost solution for maritime swarm warfare tested secretly in the Black Sea.</li>
              <li><strong>The Human Toll:</strong> Iranian officials report over 1,300 civilian deaths from these strikes, including 175 at a girls' school. The Pentagon has not confirmed if AI targeting was responsible for the school hit, citing "outdated intelligence."</li>
            </ul>
            <p>
              This is the end of the "human-in-the-loop" era as we know it. The Pentagon is prioritizing "risk tolerance" over perfection, essentially deciding that AI "hallucinations" and "drift" are acceptable costs for the speed required to deter China in 2027. By treating AI startups like national security assets (or threats), the U.S. government is forcing Silicon Valley into a wartime economy. If these autonomous systems continue to "go rogue" during tests, the risk isn't just a botched mission — it's an accidental, AI-triggered escalation into global conflict.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Manson, K. (2026, March 12). 'God, It's Terrifying': How the Pentagon Got Hooked on AI War Machines.{' '}
              <a href="https://www.bloomberg.com/news/features/2026-03-12/iran-war-tests-project-maven-us-ai-war-strategy" target="_blank" rel="noopener noreferrer">Bloomberg Businessweek</a>.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/features/2026-03-12/iran-war-tests-project-maven-us-ai-war-strategy"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg Businessweek</span>
            <span className="news-video-link-title">'God, It's Terrifying': How the Pentagon Got Hooked on AI War Machines ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'china-oil-reserves-great-wall-bloomberg',
      section: 'Above Bored',
      title: `China's 1.4 Billion Barrel "Great Wall of Oil"`,
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              While war in the Middle East has sent oil prices swinging toward $120/bbl and forced nations like Japan and Thailand to declare force majeure, China is sitting on a historic cushion. Thanks to a decade-long strategy of "energy security first," Beijing has transformed into a global fortress of crude.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Hoard:</strong> China has a record 851 million barrels in onshore commercial stock. When you add strategic reserves and underground caverns, that number jumps to a staggering 1.4 billion barrels — nearly triple the U.S. Strategic Petroleum Reserve (415M).</li>
              <li><strong>The Pivot:</strong> By prioritizing Russian pipelines and doubling its intake from Brazil since 2021, China has bypassed the most dangerous seaborne shipping routes.</li>
              <li><strong>The Buffer:</strong> Experts estimate these reserves could keep China running for six months even if Middle Eastern imports were cut off entirely.</li>
            </ul>
            <p>
              This isn't just about storage — it's about leverage. While regional competitors scramble for supply, China's refiners are using this "breathing space" to maintain domestic stability, proving that in 2026, the best defense is a massive inventory.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Bloomberg. (2026, March 11). China's energy security push pays off as war roils Asia refiners.{' '}
              <a href="https://www.japantimes.co.jp/news/2026/03/11/asia-pacific/china-security-war-asia-refiners/" target="_blank" rel="noopener noreferrer">The Japan Times</a>.
            </p>
          </div>
          <a
            href="https://www.japantimes.co.jp/news/2026/03/11/asia-pacific/china-security-war-asia-refiners/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Japan Times</span>
            <span className="news-video-link-title">China's energy security push pays off as war roils Asia refiners ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'north-korea-cruise-missile-destroyer-nknews',
      section: 'Above Bored',
      title: "Kim Jong Un's Remote-Control Naval Pivot",
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Pyongyang just conducted its second major naval test in a week, and the footage is a wake-up call for regional missile defense. From the deck of the No. 51 Choe Hyon — North Korea's newest and most advanced destroyer — leader Kim Jong Un oversaw a high-precision strike with a futuristic twist.
            </p>
            <h3>Key Highlights</h3>
            <ul>
              <li><strong>Remote Warfare:</strong> Kim executed the launch remotely via video link, specifically testing "command and control systems" for wartime scenarios.</li>
              <li><strong>The Test:</strong> Six strategic cruise missiles were launched in rapid succession, flying for 10,138 seconds (~169 minutes) — just shy of North Korea's all-time endurance record.</li>
              <li><strong>The Strategic Shift:</strong> Kim has ordered that all future 5,000-ton and 8,000-ton warships ditch traditional automatic cannons. Instead, they will be outfitted with supersonic weapons systems to maximize long-range nuclear strike capacity.</li>
            </ul>
            <p>
              This test marks a "multi-faceted operational stage" for North Korea's nuclear force. By sacrificing close-in defense guns for supersonic missiles, the DPRK is signaling a total commitment to a "nuclear trigger" naval strategy, designed to keep adversaries at a distance.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Zwirko, C. (2026, March 11). North Korea conducts second cruise missile test from new warship in last week.{' '}
              <a href="https://www.nknews.org/2026/03/north-korea-conducts-second-cruise-missile-test-from-new-warship-in-last-week/" target="_blank" rel="noopener noreferrer">NK News</a>.
            </p>
          </div>
          <a
            href="https://www.nknews.org/2026/03/north-korea-conducts-second-cruise-missile-test-from-new-warship-in-last-week/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">NK News</span>
            <span className="news-video-link-title">North Korea conducts second cruise missile test from new warship in last week ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'postliberal-superpower-foreign-affairs',
      section: 'Above Bored',
      title: 'The Postliberal Superpower: What Abandoning Democratic Allies Will Cost America',
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Michael Carpenter, a former Biden-era NSC official and ambassador to the OSCE, argues that the Trump administration's foreign policy is best understood not as realpolitik but as the projection of a postliberal ideology. Despite realist rhetoric — Monroe Doctrine, Iran strikes, "hardnosed realism" — the U.S. has dismantled institutions promoting democratic norms (USAID, U.S. Institute of Peace, NED, Voice of America, Radio Free Europe) while moderating toward Russia and China and directing its sharpest policies at democratic allies like Canada and the EU.
            </p>
            <p>
              Carpenter draws parallels with Georgia, Hungary, and Slovakia: when illiberal leaders hollow out domestic institutions, they tend to accommodate authoritarian powers and distance themselves from democratic partners. In the U.S. case, Washington has voted against a European UN resolution on Ukraine withdrawal (aligning with Russia and pariah states), exempted Russia from "Liberation Day" tariffs, ended direct military aid to Kyiv, and sought a Ukraine peace deal favorable to Moscow. With China, the administration has muted confrontational rhetoric, reportedly urged Taiwan's Lai to avoid U.S. visits, delayed a weapons sale to Taiwan, and relaxed Nvidia export controls.
            </p>
            <p>
              The Western Hemisphere pivot — strikes in the Caribbean, cartel designation, Maduro's removal, Greenland — reflects domestic political imperatives rather than great-power consolidation. Carpenter frames the Iran decapitation strikes and Greenland initiative as legacy-building maneuvers that override strategic logic. Pew polling shows allied publics increasingly distrust Trump on world affairs. Carpenter concludes that a postliberal foreign policy that treats alliances as encumbrances risks squandering the shared democratic foundation that has underpinned American strength for decades.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Carpenter, M. (2026, March 9). The Postliberal Superpower: What Abandoning Democratic Allies Will Cost America.{' '}
              <a href="https://www.foreignaffairs.com/united-states/postliberal-superpower" target="_blank" rel="noopener noreferrer">Foreign Affairs</a>.
            </p>
          </div>
          <a
            href="https://www.foreignaffairs.com/united-states/postliberal-superpower"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Foreign Affairs</span>
            <span className="news-video-link-title">The Postliberal Superpower: What Abandoning Democratic Allies Will Cost America — Read on Foreign Affairs ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'mojtaba-khamenei-supreme-leader',
      section: 'Above Bored',
      title: `Mojtaba Khamenei Named Iran's Next Supreme Leader`,
      date: 'March 8, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Following the death of Ayatollah Ali Khamenei in a U.S.-Israeli strike on February 28, 2026,
              Iran's Assembly of Experts has officially tapped his son, Mojtaba Khamenei, to be the country's
              next Supreme Leader. The decision, announced on March 8, 2026, marks the second succession in the
              history of the Islamic Republic and signals a stance of defiance amid active conflict.
            </p>
            <h3>A Shift to Hereditary Rule</h3>
            <p>
              Mojtaba's appointment is controversial because the 1979 Revolution was fought specifically to end
              hereditary monarchy. However, his deep ties to the Islamic Revolutionary Guard Corps (IRGC) and his
              experience managing his father's office made him the pragmatic choice for a regime currently at war.
            </p>
            <h3>Security and Secrecy</h3>
            <p>
              The Assembly of Experts reportedly conducted deliberations and votes virtually after their headquarters
              in Tehran and Qom were targeted by Israeli airstrikes. The announcement was delayed due to fears that
              Israel would immediately target any named successor.
            </p>
            <h3>U.S. and Israeli Warnings</h3>
            <p>
              The IDF stated it would "not hesitate to target" anyone seeking to lead or appoint a leader to the
              "war machine" against Israel. President Trump asserted that he expected to have "approval" over the
              choice, warning that a leader without U.S. backing "is not going to last long."
            </p>
            <h3>Candidate Profile</h3>
            <p>
              The 56-year-old Mojtaba has been under U.S. sanctions since 2019. He is known for his close coordination
              with the Quds Force and the Basij paramilitary, having long acted as an informal chief of staff for his
              father.
            </p>
            <p>
              By choosing a powerful insider deeply embedded in the military apparatus, Iran's clerical establishment
              is prioritizing stability and continuity over the ideological optics of avoiding hereditary succession.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              George, S. (2026, March 8). Iran's next supreme leader, Ali Khamenei's son, is a powerful regime insider.{' '}
              <a href="https://www.washingtonpost.com/world/2026/03/08/iran-supreme-leader-ali-khamenei-son-mojtaba/" target="_blank" rel="noopener noreferrer">The Washington Post</a>.
            </p>
          </div>
          <a
            href="https://www.washingtonpost.com/world/2026/03/08/iran-supreme-leader-ali-khamenei-son-mojtaba/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Washington Post</span>
            <span className="news-video-link-title">Iran's next supreme leader, Ali Khamenei's son, is a powerful regime insider — Read on WaPo ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'iran-ships-china',
      section: 'Above Bored',
      title: 'Laden Iranian Ships Depart China Amid Conflict',
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
          <p>
            Two cargo ships owned by the state-owned Islamic Republic of Iran Shipping Lines (IRISL) —
            the <strong>Shabdis</strong> and the <strong>Barzin</strong> — departed the Gaolan port in
            Zhuhai, China this week carrying suspected military-grade chemicals, according to a visual
            forensics investigation by The Washington Post.
          </p>
          <h3>The Cargo</h3>
          <p>
            Experts and former Treasury officials believe the ships are transporting <strong>sodium
            perchlorate</strong>, a critical precursor for solid rocket fuel essential to Iran's ballistic
            missile program. The chemical has very few civilian applications outside of fireworks.
          </p>
          <h3>The Context</h3>
          <p>
            The shipments are particularly notable because they occurred while the United States and Iran
            are in a state of active combat. Analysts suggest Beijing's decision not to use administrative
            delays or customs holds to stop the vessels represents a <strong>deliberate policy choice to
            support Tehran</strong> despite the regional war.
          </p>
          <h3>Tracking Data</h3>
          <p>
            Analysis of draft data — how deep a ship sits in the water — confirmed both vessels took on
            significant weight before departing. The <strong>Barzin</strong> is expected to arrive at
            Bandar Abbas, a site recently struck by U.S. and Israeli forces, on <strong>March 14</strong>.
            The <strong>Shabdis</strong> is slated for Chabahar on <strong>March 16</strong>.
          </p>
          <h3>Sanctions Evasion</h3>
          <p>
            IRISL is currently under U.S., British, and EU sanctions. The U.S. State Department identifies
            the firm as the primary shipping line for Iranian weapons proliferators.
          </p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Lee, J. S., &amp; Kelly, M. (2026, March 7). Laden Iranian ships depart Chinese port tied to key military chemicals.{' '}
            <a href="https://www.washingtonpost.com/investigations/2026/03/07/laden-iranian-ships-depart-chinese-port-tied-key-military-chemicals/" target="_blank" rel="noopener noreferrer">The Washington Post</a>.
            Ship-tracking and satellite data: Planet Labs, Kpler, Pole Star Defense.
          </p>
        </div>
          <a
            href="https://www.washingtonpost.com/investigations/2026/03/07/laden-iranian-ships-depart-chinese-port-tied-key-military-chemicals/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Washington Post</span>
            <span className="news-video-link-title">Laden Iranian ships depart Chinese port tied to key military chemicals — Read on WaPo ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'trump-defense-titans',
      section: 'Above Bored',
      title: 'Trump Meets Defense Titans to Supercharge U.S. Weapons Output',
      date: 'March 6, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              President Trump met Friday with top U.S. defense industry executives — including CEOs from
              Lockheed Martin, Northrop Grumman, RTX, Boeing, Honeywell, and L3Harris — as his administration
              looks to accelerate weapons production while military operations against Iran continue.
            </p>
            <p>
              The White House emphasized the session was scheduled weeks in advance and was not convened in
              response to battlefield shortages. Press Secretary Karoline Leavitt confirmed stockpiles are
              sufficient to "finish Operation Epic Fury, no matter how long it lasts," while underscoring
              Trump's push for a historic surge in American-made weapons.
            </p>
            <h3>The Supply Equation</h3>
            <p>
              During the 2025 12-day Iran conflict, U.S. forces fired more than 150 THAAD interceptors —
              roughly a quarter of the global inventory — to shield Israel and U.S. assets. Patriot PAC-3 MSE
              missiles are currently produced at roughly 600–650 annually, with replenishment timelines
              measured in months or years rather than weeks.
            </p>
            <p>
              Central Command chief Adm. Brad Cooper confirmed Iran's missile attacks have decreased 90%
              since the start of the conflict. But defense planners note the same high-end systems deployed
              in the Middle East are also committed to Ukraine and the Indo-Pacific — a "zero-sum" competition
              for inventory across theaters.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Phillips, M. (2026, March 6). Trump rallies defense titans to surge weapons output as Iran War rages.{' '}
              <a href="https://www.foxnews.com/politics/trump-rallies-defense-titans-surge-weapons-output-iran-war-rages.amp" target="_blank" rel="noopener noreferrer">Fox News</a>.
            </p>
          </div>
          <a
            href="https://www.foxnews.com/politics/trump-rallies-defense-titans-surge-weapons-output-iran-war-rages.amp"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Fox News</span>
            <span className="news-video-link-title">Trump rallies defense titans to surge weapons output as Iran War rages — Read on Fox News ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'dow-sinks-march5',
      section: 'Above Bored',
      title: 'March 5 Market Recap: Middle East Conflict Sends Oil Soaring, Dow Tumbles',
      date: 'March 5, 2026',
      body: () => (
        <div className="news-article-body">

          {/* ── Ticker bar ── */}
          <div className="fish-ticker-bar">
            <div className="fish-ticker-item fish-ticker-down">
              <span className="fish-ticker-label">Dow Jones</span>
              <span className="fish-ticker-value">47,954.74</span>
              <span className="fish-ticker-change">▼ 784.67 pts / 1.61%</span>
            </div>
            <div className="fish-ticker-item fish-ticker-down">
              <span className="fish-ticker-label">S&amp;P 500</span>
              <span className="fish-ticker-value">6,830.71</span>
              <span className="fish-ticker-change">▼ 0.56%</span>
            </div>
            <div className="fish-ticker-item fish-ticker-down">
              <span className="fish-ticker-label">Nasdaq</span>
              <span className="fish-ticker-value">22,748.99</span>
              <span className="fish-ticker-change">▼ 0.26%</span>
            </div>
            <div className="fish-ticker-item fish-ticker-up">
              <span className="fish-ticker-label">WTI Crude</span>
              <span className="fish-ticker-value">$81.01</span>
              <span className="fish-ticker-change">▲ 8.5% — Highest since 2024</span>
            </div>
            <div className="fish-ticker-item fish-ticker-up">
              <span className="fish-ticker-label">Brent Crude</span>
              <span className="fish-ticker-value">$85.41</span>
              <span className="fish-ticker-change">▲ 4.9%</span>
            </div>
            <div className="fish-ticker-item fish-ticker-neutral">
              <span className="fish-ticker-label">2-yr Treasury</span>
              <span className="fish-ticker-value">3.6%</span>
            </div>
            <div className="fish-ticker-item fish-ticker-neutral">
              <span className="fish-ticker-label">10-yr Treasury</span>
              <span className="fish-ticker-value">4.15%</span>
            </div>
          </div>

          <p>
            On March 5, 2026, the Dow Jones Industrial Average plummeted nearly 800 points as geopolitical
            instability in the Middle East triggered a massive spike in energy prices. Following a strike on
            a U.S. oil tanker, West Texas Intermediate (WTI) crude experienced its largest one-day percentage
            gain since 2020, closing above $80 a barrel.
          </p>
          <p>
            Market analysts, including Ed Yardeni, suggest this conflict puts the Federal Reserve in a
            "straitjacket," where rising inflation driven by oil costs may block interest rate cuts even if
            the broader economy begins to falter — a classic stagflationary trap.
          </p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Smith, C. (2026, March 5). Stock Market News From March 5, 2026: Dow Sinks Nearly 800 Points, Oil Tops $80.{' '}
            <a href="https://www.barrons.com/livecoverage/stock-market-news-today-030526" target="_blank" rel="noopener noreferrer">Barron's</a>.
          </p>
        </div>
      ),
    },
    {
      slug: 'defense-dip',
      section: 'Above Bored',
      title: 'March 4 Market Recap: Why Defense Stocks Are Dipping Amid U.S. Military Success',
      date: 'March 4, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              U.S. Defense Secretary Pete Hegseth and Joint Chiefs Chair Dan Caine provided a major update Wednesday on
              Operation "Epic Fury" in Iran. While the report was filled with military successes, the stock market
              reacted with a dip for major defense contractors.
            </p>
            <h3>Key Highlights from the Pentagon</h3>
            <ul>
              <li><strong>Decisive Progress:</strong> Hegseth stated that "America is winning... decisively, devastatingly, and without mercy."</li>
              <li><strong>Air &amp; Sea Dominance:</strong> U.S. and Israeli forces have established complete air dominance and decimated Iranian naval forces.</li>
              <li><strong>Future Operations:</strong> The military is moving inland to create "freedom of maneuver," with General Caine confirming they have sufficient precision munitions for both offense and defense.</li>
              <li><strong>The Conflict Scale:</strong> To date, Iran has fired over 500 missiles and 2,000 drones.</li>
            </ul>
            <h3>The Market Reaction</h3>
            <p>
              Defense stocks often rise at the onset of fighting and retract as conflicts stabilize. The rapid success
              of U.S. forces suggests a potentially quicker end to the conflict, leading to a Wednesday morning slide:
            </p>
            <ul>
              <li><strong>Northrop Grumman (NOC):</strong> ↓ 2%</li>
              <li><strong>Lockheed Martin (LMT):</strong> ↓ 1.3%</li>
              <li><strong>L3Harris (LHX):</strong> ↓ 1.1%</li>
              <li><strong>RTX:</strong> ↓ 0.3%</li>
            </ul>
            <h3>The Bottom Line</h3>
            <p>
              Despite the daily dip, the defense sector remains incredibly strong. The iShares US Aerospace &amp;
              Defense ETF is up 60% over the last 12 months, driven by a global trend of increased national defense
              spending that isn't expected to slow down anytime soon.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Root, A. (2026, March 5). Write to Al Root at{' '}
              <a href="mailto:allen.root@dowjones.com">allen.root@dowjones.com</a>.{' '}
              <a href="https://www.barrons.com/articles/northrop-stock-price-defense-general-dynamics-82187c5e" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.foxnews.com/video/6390376279112"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Fox News</span>
            <span className="news-video-link-title">Pentagon Briefing: Operation Epic Fury — Watch on Fox News ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'march-2-recap',
      section: 'Above Bored',
      title: 'March 2 Market Recap: Energy and Defense Surge Amid Middle East Volatility',
      date: 'March 2, 2026',
      body: () => (
        <>
          <div className="news-video-wrap">
            <iframe
              src="https://www.youtube.com/embed/t7UYilld5Dc?start=349"
              title="Marco Rubio Speaks To Reporters About The Escalating Conflict With Iran"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <div className="news-article-body">
            <h3>Market Overview</h3>
            <p>U.S. stocks achieved modest gains on Monday following weekend military strikes on Iran by the U.S. and Israel. The conflict caused a surge in global oil prices and significant volatility across various market sectors.</p>
            <h3>Major Market Gainers</h3>
            <ul>
              <li><strong>Energy and Oil:</strong> West Texas Intermediate futures jumped 6.4%. Notable gainers included Venture Global, which surged 17% despite missing future guidance, as well as APA (+4.4%), ConocoPhillips (+4.2%), and Chevron (+1%).</li>
              <li><strong>Defense Contractors:</strong> The sector rallied following the strikes in the Middle East. Northrop Grumman rose 6%, RTX added 4.7%, and Lockheed Martin advanced 3.4%.</li>
              <li><strong>Technology &amp; AI Infrastructure:</strong> Coherent (+15%) and Lumentum (+12%) surged after Nvidia pledged to invest $2 billion in each company. Palantir Technologies also rose 5.8%.</li>
              <li><strong>Crypto-Related:</strong> Strategy climbed 6.3% after disclosing the purchase of 3,015 Bitcoin tokens the previous week. Bitcoin itself rose to approximately $68,966.</li>
            </ul>
            <h3>Major Market Decliners</h3>
            <ul>
              <li><strong>Clean Energy:</strong> AES sank 18% to $14.21 after agreeing to be taken private by a consortium including a BlackRock subsidiary for $15 a share.</li>
              <li><strong>Biotech:</strong> uniQure plummeted 33% after the FDA ruled that its Phase 1 and 2 test data for a Huntington's disease therapy were insufficient to prove effectiveness.</li>
              <li><strong>Travel &amp; Leisure:</strong> Cruise lines and airlines tumbled due to rising fuel costs and travel chaos. Norwegian Cruise Line fell 11%, Carnival dropped 7.6%, and American Airlines declined 4.2%.</li>
              <li><strong>Insurance:</strong> Elevance Health fell 8.1% following news that federal regulators planned to impose sanctions due to alleged data submission noncompliance.</li>
              <li><strong>Conglomerates:</strong> Berkshire Hathaway slid 4.9% after reporting a 30% drop in operating profit and no stock buybacks during Warren Buffett's final three months as CEO.</li>
            </ul>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Tatananni, M., &amp; Glover, G. (2026, March 2). These Stocks Are Today's Movers: AES, Norwegian,
              Elevance, Palantir, Lockheed, Strategy, Berkshire Hathaway, and More.{' '}
              <a href="https://www.barrons.com/articles/stock-movers-b961ba1f" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
        </>
      ),
    },
  ],

  energy: [
    {
      slug: 'battery-bleed-out-us-energy-storage-implosion',
      section: 'No Energy',
      title: 'Battery Bleed-Out: The US Energy Storage Implosion',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> The American battery gold rush has turned into a brutal graveyard as billion-dollar startups collapse under the weight of a cooling EV market and evaporated subsidies.
            </p>
            <p>
              The "battery boom" of the early 2020s has officially hit a terminal velocity—in the wrong direction. What was once a landscape of infinite venture capital and "shiny new chemistries" has transformed into a Darwinian struggle for survival. In a shock to the system, 24M Technologies—a former "unicorn" valued at over $1 billion—has reportedly shuttered its doors, signaling that even "safe bet" innovations designed to improve existing lithium-ion tech aren't immune to the current industry-wide rot.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Fall of 24M:</strong> Despite a high-pedigree founding team and a "smearing" manufacturing process designed to slash costs and hit a 1,000-mile range goal, the MIT spinoff is headed for the auction block.</li>
              <li><strong>The Domino Effect:</strong> 24M joins a growing list of 2025–2026 casualties, including sodium-ion leader Natron Energy and battery-swapping pioneer Ample, both of which filed for bankruptcy or ceased operations within the last six months.</li>
              <li><strong>The Funding Freeze:</strong> Investors have lost their "appetite for innovation." As the U.S. government guts key Inflation Reduction Act (IRA) incentives, venture capital is retreating from long-term hardware bets in favor of immediate profitability.</li>
              <li><strong>The Global Disconnect:</strong> While the U.S. industry stumbles, China's battery giants (like CATL and BYD) continue to tighten their stranglehold on the global supply chain, leveraging scale and state support to outpace Western startups.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This isn't just a market correction; it's a strategic crisis for U.S. energy independence. By allowing these innovative "bridge technologies" to fail, the U.S. risks ceding the entire next generation of transportation and grid storage to Chinese dominance. As automakers scale back EV plans and factory blueprints are shredded, the "American Battery Belt" is looking less like a manufacturing revolution and more like a cautionary tale of over-hyped capital meeting a harsh regulatory and macroeconomic reality.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Brutal times for the US battery industry.{' '}
              <a href="https://www.technologyreview.com/2026/03/12/1134197/us-battery-industry/" target="_blank" rel="noopener noreferrer">MIT Technology Review</a>, March 12, 2026.
            </p>
          </div>
          <a
            href="https://www.technologyreview.com/2026/03/12/1134197/us-battery-industry/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">MIT Technology Review</span>
            <span className="news-video-link-title">Brutal times for the US battery industry ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'copper-fcx-ai-discount-barrons',
      section: 'No Energy',
      title: 'The Red Metal\'s AI Discount',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              While the Dow bleeds over oil prices, the "Doctor of Economics" — copper — is presenting a tactical entry point. Freeport-McMoRan, the U.S.'s largest copper miner, has pulled back 10% from its highs on Iran war fears, but the underlying thesis remains bulletproof: the AI buildout and global electrification require massive amounts of wiring that simply don't exist yet. With analysts forecasting a 25% price surge for the metal by 2027, this dip looks less like a crash and more like a gift.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The AI Multiplier:</strong> Data centers and the AI infrastructure boom are the primary drivers for a "future-facing" copper rally. Bank of America predicts copper will hit $7.26 a pound by late next year.</li>
              <li><strong>The Valuation Gap:</strong> FCX trades at 21x 2026 earnings, significantly cheaper than peer Southern Copper at 29x.</li>
              <li><strong>The Grasberg Recovery:</strong> A tragic mudslide in Indonesia last year hampered production, but visibility on the ramp-up is improving. Jefferies expects the mine to reach 100% capacity by late 2027.</li>
              <li><strong>The Washington Tailwind:</strong> The Trump administration has labeled copper a "critical mineral." CEO Kathleen Quirk has hinted at potential federal support for domestic production, though the company's $4B cash pile means they don't actually need it.</li>
              <li><strong>The Technical Floor:</strong> Chartists note a "double bottom" pattern forming, with support above $58.50 and price targets as high as $77 by mid-2026.</li>
            </ul>
            <p>
              Copper is the physical backbone of the digital age. You cannot have "Sovereign AI" or an energy transition without the red metal. While geopolitical tensions cause short-term cyclical noise, the structural deficit in copper supply is a multi-year reality. FCX offers a rare combination of a healthy balance sheet, a dominant market position, and a valuation discount due to temporary operational hurdles.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              La Monica, P. R. (2026, March 12). This Copper Stock Is Worth Mining. The Metal's Boom Is On.{' '}
              <a href="https://www.barrons.com/articles/copper-stock-freeport-mcmoran-worth-mining-metals-boom-98e6ef5e?mod=hp_STOCKPICKS_1" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/articles/copper-stock-freeport-mcmoran-worth-mining-metals-boom-98e6ef5e?mod=hp_STOCKPICKS_1"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">This Copper Stock Is Worth Mining. The Metal's Boom Is On ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'aramco-east-west-pipeline-hormuz-barrons',
      section: 'No Energy',
      title: "Aramco's $100B Pipeline Gamble to Bypass the Strait",
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              With the war in Iran being labeled the "biggest crisis for oil markets in history" by Aramco CEO Amin Nasser, Saudi Arabia is days away from hitting full capacity on its critical East-West pipeline. As tankers refuse to risk the Strait of Hormuz — which typically handles 17 million barrels a day — the Kingdom is trying to reroute its global supply to the Red Sea.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Pipeline Limit:</strong> The East-West line can move 5 million barrels of crude daily. While impressive, that only covers about 70% of Aramco's usual 7-million-barrel export volume.</li>
              <li><strong>The "Hormuz Bottleneck":</strong> Currently, the Strait is effectively blocked. While oil prices cooled to $89.38 on Tuesday (after hitting a terrifying $119.50 Monday), the physical disruption remains catastrophic.</li>
              <li><strong>Production "Pulsing":</strong> Nasser confirmed Aramco isn't shutting wells entirely; instead, they are using "well restrictions" that allow them to ramp back up in days, not weeks, once the crisis abates.</li>
              <li><strong>The Bottom Line for Investors:</strong> Aramco is raising its dividend by 3.5%, but high prices are a double-edged sword. When oil crosses $100, the company's royalty rate to the Saudi government spikes to a massive 80%.</li>
            </ul>
            <p>
              This isn't just a logistics update — it's a stress test of the global energy heartland. If the East-West pipeline hits its ceiling and the Strait remains closed, the world loses a massive chunk of its daily refined product and crude supply, regardless of how much oil is sitting in the ground.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Salzman, A. (2026, March 10). How Saudi Aramco Plans to Move Oil Without the Strait of Hormuz.{' '}
              <a href="https://www.barrons.com/articles/saudi-aramco-oil-prices-pipeline-hormuz-iran-395c4db2" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/articles/saudi-aramco-oil-prices-pipeline-hormuz-iran-395c4db2"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">How Saudi Aramco Plans to Move Oil Without the Strait of Hormuz ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'iea-strategic-reserve-release-record-wsj',
      section: 'No Energy',
      title: 'IEA Triggers the "Nuclear Option" for Oil Reserves',
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              In an emergency move in Paris, the International Energy Agency has proposed a record-shattering release of strategic reserves. Aimed at breaking the "near-total closure" of the Strait of Hormuz, this release is set to dwarf the 182 million barrels deployed during the 2022 Ukraine invasion.
            </p>
            <h3>Key Highlights</h3>
            <ul>
              <li><strong>The "124-Day" Shield:</strong> IEA members currently hold 1.2 billion barrels in public stocks. At current consumption rates, that represents roughly 124 days of lost supply from the Persian Gulf.</li>
              <li><strong>The Price Rollercoaster:</strong> Since strikes began on Feb 28, oil prices have soared as much as 40%. While crude dipped below $84 on Tuesday, diesel and fuel prices are still "skyrocketing," threatening a global inflationary spiral.</li>
              <li><strong>Historical Precedent:</strong> The IEA is looking back to 1991's Operation Desert Storm, where a coordinated release saw oil prices drop 20% in a single day.</li>
              <li><strong>The Decision:</strong> Energy officials from 32 member countries are expected to finalize the vote on Wednesday. A single objection could stall the plan, but the pressure to act is at an all-time high.</li>
            </ul>
            <p>
              The IEA was created in 1974 specifically for this "nightmare scenario" — a total blockade of Middle Eastern oil. By flooding the market now, Western allies are hoping to prevent a stock market correction and pain at the pump from turning into a full-blown global recession.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Dalton, M., & Pancevski, B. (2026, March 10). IEA Proposes Largest Ever Oil Release From Strategic Reserves.{' '}
              <a href="https://www.wsj.com/business/energy-oil/iea-proposes-largest-ever-oil-release-from-strategic-reserves-275f4e5c" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/business/energy-oil/iea-proposes-largest-ever-oil-release-from-strategic-reserves-275f4e5c"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">IEA Proposes Largest Ever Oil Release From Strategic Reserves ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'senate-tax-bill-clean-energy-cutbacks',
      section: 'No Energy',
      title: 'Senate Tax Bill Seeks to Gut Clean Energy; Musk Calls It "Utterly Insane"',
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Congressional Republicans are poised to reverse U.S. energy policy by wiping out hundreds of billions in incentives for solar, wind, battery storage and consumer purchases of EVs, heat pumps and rooftop solar. The Senate's tax bill, which cleared a key procedural hurdle in June 2025, contains strict green energy cutbacks and imposes a new tax on existing wind and solar farms that include materials from foreign entities like China — a blow to the renewables industry, which relies heavily on Chinese supply chains. Sen. Ron Wyden (D-Ore.) called it a "death sentence" for green energy.
            </p>
            <p>
              Elon Musk posted on X that the bill "will destroy millions of jobs in America and cause immense strategic harm to our country! Utterly insane and destructive. It gives handouts to industries of the past while severely damaging industries of the future." Consumer subsidies for EVs, heat pumps and rooftop solar are set to expire within months. NorSun put its $620 million Tulsa solar wafer factory on hold indefinitely; Talon PV and ENGIE have cut or scaled back U.S. investment. Energy Innovation and Rhodium modeling suggests the rollbacks could cost as many as 830,000 jobs by 2030 and raise electricity prices.
            </p>
            <p>
              The Clean Energy Buyers Association — backed by tech firms — warns that robust wind and solar incentives are "essential to winning the artificial intelligence (AI) race." NextEra CEO John Ketchum wrote that with nuclear years away and fossil fuel turbines back-ordered into the 2030s, "America's only option is to build wind, solar, and battery storage." Energy Secretary Chris Wright testified that wind and solar are "just a parasite on the grid." Some red-state Republicans, including Sen. Thom Tillis of North Carolina, have balked at the repeal; North Carolina has drawn an estimated $16 billion in clean energy investment under the current credits.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Halper, E., &amp; Osaka, S. (2025, June 29). Senate's tax bill seeks to gut clean energy; Musk calls it "utterly insane".{' '}
              <a href="https://www.washingtonpost.com/climate-environment/2025/06/28/solar-green-energy-tax-bill-trump/" target="_blank" rel="noopener noreferrer">The Washington Post</a>.
            </p>
          </div>
          <a
            href="https://www.washingtonpost.com/climate-environment/2025/06/28/solar-green-energy-tax-bill-trump/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Washington Post</span>
            <span className="news-video-link-title">Senate's tax bill seeks to gut clean energy; Musk calls it "utterly insane" — Read on WaPo ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'israel-iran-fuel-strikes',
      section: 'No Energy',
      title: `Israel's Iran Fuel Strikes Roil Oil Markets`,
      date: 'March 8, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              On March 8, 2026, Israel conducted airstrikes against 30 Iranian fuel depots, causing
              significant infrastructure damage and visible fires in Tehran. This action has sparked the
              first major diplomatic rift between the U.S. and Israel since the current war began eight days ago.
            </p>
            <h3>Diplomatic Friction</h3>
            <p>
              While Israel notified the U.S. of the planned strikes in advance, U.S. officials were
              reportedly surprised by the wide-ranging scale of the operation. A senior U.S. official
              described the strikes as a bad idea, and an Israeli official characterized the U.S. reaction
              as one of disbelief.
            </p>
            <h3>U.S. Energy Concerns</h3>
            <p>
              The Biden administration fears the strikes could prove counterproductive by rallying the
              Iranian public to support their regime. Furthermore, there is significant concern that the
              visuals of burning fuel depots will spook global oil markets and lead to rising gas prices. A
              Trump adviser noted that the president "wants to save the oil" and avoid price hikes.
            </p>
            <h3>Israeli Justification</h3>
            <p>
              The Israeli Defense Forces (IDF) stated that the targeted depots supply fuel to Iranian
              military organs. An Israeli military official added that the strikes were intended to deter
              Iran from continuing to target Israeli civilian infrastructure.
            </p>
            <h3>Iranian Threats</h3>
            <p>
              In response to the strikes, Iranian officials—including the parliament speaker and a
              spokesperson for the Khatam al-Anbiya headquarters—warned that if attacks on oil
              infrastructure continue, Tehran will retaliate immediately. They warned that such escalation
              could potentially drive oil prices to $200 per barrel.
            </p>
            <p>
              The issue is expected to be addressed at senior political levels between the U.S. and Israel
              to reconcile their diverging views on the war's strategy.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Ravid, B., &amp; Caputo, M. (2026, March 8). Scoop: U.S. dismayed by Israel's Iran fuel strikes, sources say.{' '}
              <a href="https://www.axios.com/2026/03/08/us-dismayed-israel-iran-fuel-strikes" target="_blank" rel="noopener noreferrer">Axios</a>.
            </p>
          </div>
          <a
            href="https://www.axios.com/2026/03/08/us-dismayed-israel-iran-fuel-strikes"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Axios</span>
            <span className="news-video-link-title">Scoop: U.S. dismayed by Israel's Iran fuel strikes — Read on Axios ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'cuba-blackout-oil-siege',
      section: 'No Energy',
      title: "Cuba's Grid on the Edge: Blackout, Oil Siege, and Solar Stopgaps",
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Cuba's largest power plant is back online — but for the 7 million people who spent four days
              in total darkness, this is not a victory lap. It's a brief reprieve in what is becoming a
              structural collapse.
            </p>
            <p>
              On March 4, a boiler failure at the Antonio Guiteras thermoelectric plant — the backbone of
              the national grid — triggered a cascading blackout across the entire island. Havana went dark.
              Schools, public transport, and airports ground to a halt. Protests broke out in several
              provinces where outages stretched beyond 20 hours a day.
            </p>
            <h3>The Oil Siege</h3>
            <p>
              The technical failure landed on top of an already critical energy shortage engineered, in
              part, from Washington. Following the U.S.-backed removal of Venezuelan President Nicolás Maduro
              in early 2026, Cuba's lifeline of roughly 35,000 barrels of oil per day was cut overnight.
              Executive Order 14380, signed by President Trump on January 29, extended the pressure further —
              threatening steep tariffs on any third-party country that steps in to fill the gap. Mexico,
              which had been quietly supplying oil, has since largely halted its shipments.
            </p>
            <p>
              The Trump administration has been explicit about its endgame. Officials have openly discussed
              "regime change in Havana" as a near-term goal, with some suggesting a timeline as early as
              late 2026. Trump himself floated the idea of a "friendly takeover" of the island.
            </p>
            <h3>Solar Stopgaps and Their Limits</h3>
            <p>
              Cuba has not been passive. Over the past year, the government connected 49 Chinese-built solar
              parks, pushing renewables to roughly 20% of consumption — a significant jump for a nation
              historically dependent on aging Soviet-era thermal infrastructure. The problem is storage.
              Without battery backup, the solar grid can't carry the load after dark. The moment thermal
              capacity fails at night, the entire system collapses — exactly what happened on March 4.
            </p>
            <h3>The Current Picture</h3>
            <p>
              Even with Guiteras repaired, Cuba's Electric Union reports only 1,000 megawatts available
              against a national demand that far exceeds that figure. Jet fuel remains unavailable at nine
              airports through at least mid-March. The government has shuttered schools and universities
              and suspended public transport indefinitely.
            </p>
            <p>
              The solar panels are running. The plant is back. But Cuba's grid is operating at less than
              half of what the island needs — and winter hasn't made things easier.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Al Jazeera Staff &amp; AP. (2026, March 7). Cuba says electricity plant successfully repaired after large blackout.{' '}
              <a href="https://www.aljazeera.com/news/2026/3/7/cuba-says-electricity-plant-successfully-repaired-after-large-blackout" target="_blank" rel="noopener noreferrer">Al Jazeera</a>.
            </p>
          </div>
          <a
            href="https://www.aljazeera.com/news/2026/3/7/cuba-says-electricity-plant-successfully-repaired-after-large-blackout"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Al Jazeera</span>
            <span className="news-video-link-title">Cuba says electricity plant successfully repaired after large blackout — Read on Al Jazeera ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'skyward-wildfire-lightning',
      section: 'No Energy',
      title: 'Startup Skyward Wildfire Aims to "Stop Lightning" Using Weather Modification',
      date: 'March 3, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Vancouver-based startup Skyward Wildfire has raised $5.7 million to scale a controversial
              technology designed to prevent wildfires at               the source: by suppressing lightning strikes
              before they ignite. The company seeds clouds with "inert materials" — likely metallic chaff —
              deployed via planes and drones to neutralize storm charges mid-atmosphere. (
              <a href="https://www.skywardwildfire.com/" target="_blank" rel="noopener noreferrer">skywardwildfire.com</a>
              )
            </p>
            <p>
              Skyward recently retracted earlier claims of "100% effectiveness" following scientific
              scrutiny, but preliminary trials conducted with Canadian wildfire agencies reportedly showed
              significant reductions in lightning activity. The company is now scaling toward broader
              deployment as wildfire seasons intensify across North America.
            </p>
            <p>
              Researchers remain cautious. Decades of inconclusive data on cloud seeding and legitimate
              concerns about unintended environmental consequences of large-scale weather modification
              have kept the scientific community from endorsing the approach — even as the urgency of
              wildfire prevention grows.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Temple, J. (2026, March 3). This startup claims it can stop lightning and prevent catastrophic wildfires.{' '}
              <a href="https://www.technologyreview.com/2026/03/03/1133848/this-startup-claims-it-can-stop-lightning-and-prevent-catastrophic-wildfires/" target="_blank" rel="noopener noreferrer">MIT Technology Review</a>.
            </p>
          </div>
          <a
            href="https://www.technologyreview.com/2026/03/03/1133848/this-startup-claims-it-can-stop-lightning-and-prevent-catastrophic-wildfires/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">MIT Technology Review</span>
            <span className="news-video-link-title">This startup claims it can stop lightning and prevent catastrophic wildfires — Read on MIT Tech Review ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'triple-nuclear-declaration',
      section: 'No Energy',
      title: '33 Nations Pledge to Triple Nuclear Capacity by 2050',
      date: 'March 5, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              As of March 5, 2026, 33 nations have officially endorsed the Declaration to Triple Nuclear
              Energy — a landmark ministerial pledge committing participating countries to tripling global
              nuclear energy capacity by 2050 to help meet net-zero emissions targets.
            </p>
            <p>
              The coalition includes a diverse range of global powers and emerging economies, among them the
              United States, France, the United Kingdom, Japan, and the Republic of Korea, as well as nations
              like Ghana, Kazakhstan, and Ukraine. The declaration emphasizes nuclear energy's role in
              providing reliable, carbon-free baseload power to combat climate change.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Declaration to Triple Nuclear Energy. (2026, March 5).{' '}
              <a href="https://world-nuclear.org/net-zero-nuclear/tripling-pledges/declaration-to-triple-nuclear-energy" target="_blank" rel="noopener noreferrer">World Nuclear Association</a>.
            </p>
          </div>
          <a
            href="https://world-nuclear.org/net-zero-nuclear/tripling-pledges/declaration-to-triple-nuclear-energy"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">World Nuclear Association</span>
            <span className="news-video-link-title">Declaration to Triple Nuclear Energy — Read on World Nuclear Association ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'terrapower-wyoming',
      section: 'No Energy',
      title: "U.S. Greenlights Bill Gates-Backed Nuclear Project",
      date: 'March 4, 2026',
      body: () => (
        <div className="news-article-body">
          <p>
            The future of energy just got a major boost. The Nuclear Regulatory Commission (NRC) has officially approved
            construction for TerraPower's innovative small nuclear reactor in Kemmerer, Wyoming. This marks the first
            time in nearly a decade that the NRC has approved a new commercial reactor design.
          </p>
          <h3>The Breakdown</h3>
          <ul>
            <li><strong>The Tech:</strong> A 345-megawatt sodium-cooled "Natrium" reactor.</li>
            <li><strong>The Power:</strong> Includes a unique energy storage system that can temporarily ramp up output to 500 MW.</li>
            <li><strong>The Timeline:</strong> TerraPower plans to break ground in the coming weeks, with the goal of being operational by the early 2030s.</li>
            <li><strong>The Location:</strong> It will be built near an existing coal-fired plant.</li>
          </ul>
          <h3>Strategic Importance</h3>
          <p>
            This project is a cornerstone of a broader U.S. push to quadruple nuclear capacity by 2050. To support this,
            the Department of Energy is providing up to $2 billion in funding.
          </p>
          <p>
            Additionally, the U.S. is working to secure its own fuel supply chain. The Natrium reactor runs on HALEU
            (high-assay low-enriched uranium)—a fuel traditionally sourced from Russia. In January, the DOE awarded
            $900 million each to Centrus Energy and General Matter to kickstart domestic production of this specialized
            fuel.
          </p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Gardner, T. (2026, March 4). U.S. approves construction of Gates-backed TerraPower reactor in Wyoming.{' '}
            <a href="https://www.reuters.com/business/energy/us-approves-construction-gates-backed-terrapower-reactor-wyoming-2026-03-04/" target="_blank" rel="noopener noreferrer">Reuters</a>.
          </p>
        </div>
      ),
    },
  ],

  meds: [
    {
      slug: 'judicial-firewall-hhs-vaccine-overhaul-halted',
      section: 'Rx',
      title: 'Judicial Firewall: HHS Vaccine Overhaul Halted',
      date: 'March 16, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> A federal judge freezes RFK Jr.'s "skeptical" vaccine agenda, ruling that the mass firing of experts and slashing of childhood shot lists bypassed the law.
            </p>
            <p>
              The Trump administration's ambitious attempt to dismantle decades of public health consensus has hit a major legal roadblock. On Monday, a federal judge blocked Health and Human Services (HHS) Secretary Robert F. Kennedy Jr. from implementing a pared-down childhood immunization schedule that would have dropped recommendations for six major vaccines, including Covid-19, Hepatitis A and B, and the flu. The ruling effectively paralyzes the newly overhauled Advisory Committee on Immunization Practices (ACIP), citing "glaring gaps" in the expertise of Kennedy's hand-picked appointees and a blatant disregard for federal procedural standards.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Injunction:</strong> Judge Brian E. Murphy issued a blistering order, ruling that HHS improperly bypassed technical advisory panels and abandoned "technical knowledge and expertise" when it slashed the recommended childhood vaccine list from 17 diseases to 11.</li>
              <li><strong>The "Clean Slate" Crisis:</strong> The court put a hold on Kennedy's new committee appointments, noting that only 6 of the 15 members had "meaningful experience" in vaccines. This effectively cancels this week's planned ACIP meetings, as the panel currently has no legal membership.</li>
              <li><strong>The Policy Pivot:</strong> The blocked guidelines had moved vaccines for Covid-19, Hep A/B, Flu, Meningococcal, and Rotavirus from "universal recommendations" to "discretionary," placing the burden of decision entirely on parents and individual doctors.</li>
              <li><strong>The Corporate Fallout:</strong> Medical associations, including the American Academy of Pediatrics, successfully argued that the sudden shift created "market chaos," leaving pharmacists, parents, and insurance providers in a state of confusion regarding coverage and liability.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This ruling sets up a constitutional showdown over the "Administrative State" and the limits of executive power in public health. By freezing the ACIP, the court has temporarily preserved the traditional vaccine infrastructure, but it has also created a policy vacuum. For the healthcare industry, the stakes are massive: if the administration successfully overturns this decision, it could trigger a fragmented, state-by-state vaccine marketplace, fundamentally altering the business model for pharmaceutical giants and the standard of care for millions of American children.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Judge Blocks New Childhood Vaccine Recommendations.{' '}
              <a href="https://www.wsj.com/health/healthcare/judge-blocks-new-childhood-vaccine-recommendations-da83325a?mod=health_lead_pos1" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>, March 16, 2026.
            </p>
          </div>
          <a
            href="https://www.wsj.com/health/healthcare/judge-blocks-new-childhood-vaccine-recommendations-da83325a?mod=health_lead_pos1"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">Judge Blocks New Childhood Vaccine Recommendations ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'healthcare-utility-moment-indiana-vermont-economist',
      section: 'Rx',
      title: 'Healthcare\'s "Utility" Moment',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The wild west of hospital billing is facing a sheriff from both sides of the aisle. With hospital prices outstripping inflation by 3x since 2000, "red" Indiana and "blue" Vermont are deploying price caps to prevent total fiscal collapse. Whether it's threatening the tax-exempt status of non-profits or tying charges to Medicare multiples, the message is clear: the market has failed to provide competition, so the government is providing a ceiling.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Inflation Gap:</strong> Since 2000, hospital prices have skyrocketed 250%, accounting for nearly one-third of all U.S. healthcare spending and becoming the primary driver of national debt.</li>
              <li><strong>The Monopoly Problem:</strong> By 2023, nearly 50% of metropolitan areas were controlled by just one or two health systems.</li>
              <li><strong>The Indiana Strategy:</strong> Republican Governor Mike Braun is treating hospitals like power or water companies, forcing non-profits to align prices with statewide averages by 2029 or lose their lucrative tax exemptions.</li>
              <li><strong>The Vermont Multiple:</strong> Regulators are shifting to a "Medicare-Plus" model, capping commercial rates at a set multiple of federal rates, with potential savings reaching $445M annually.</li>
              <li><strong>The Counter-Argument:</strong> Hospital associations warn of "screwdrivers to oranges" comparisons and predict aggressive caps will lead to rural closures and a freeze on medical innovation.</li>
            </ul>
            <p>
              This is a fundamental reclassification of the American hospital. By shifting from a "market negotiation" model to a "regulated utility" model, states are admitting that consolidation has made traditional capitalism impossible in healthcare. If this "populist-MAGA" and "progressive-left" pincer movement succeeds, it will decimate the margins of major health systems but potentially save the employer-sponsored insurance market from total insolvency. Watch for this to become a template for the 2026 midterms as voters across the spectrum reach a breaking point on premiums.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Two very different states take aim at soaring hospital prices.{' '}
              <a href="https://www.economist.com/united-states/2026/03/12/two-very-different-states-take-aim-at-soaring-hospital-prices" target="_blank" rel="noopener noreferrer">The Economist</a>.
            </p>
          </div>
          <a
            href="https://www.economist.com/united-states/2026/03/12/two-very-different-states-take-aim-at-soaring-hospital-prices"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Economist</span>
            <span className="news-video-link-title">Two very different states take aim at soaring hospital prices ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'silicon-valley-healthcare-disruption-2026',
      section: 'Rx',
      title: "Silicon Valley's Second Shot at Disrupting Healthcare",
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              After high-profile failures like IBM Watson and the Amazon-JPMorgan "Haven" alliance, a new wave of disrupters is betting that 2026 is the year American healthcare finally digitizes. With US health spending hitting $5 trillion (20% of GDP) and family premiums soaring to $27,000, patients are ditching traditional gatekeepers for "agency" over their own biology.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The VC Surge:</strong> Following a post-pandemic slump, digital-health startups hauled in $14 billion in venture funding in 2025 — a 35% increase year-over-year.</li>
              <li><strong>Direct-to-Consumer Diagnostics:</strong> Companies like Prenuvo and Function Health are bypassing insurance entirely, selling full-body MRIs and advanced blood panels directly to patients for $1,000 to $4,500.</li>
              <li><strong>The AI Medical Assistant:</strong> OpenAI and Anthropic have launched specialized health chatbots for their 800 million weekly users. These tools aren't just "Googling" symptoms — they are identifying patterns in wearable data and explaining complex labs in plain English.</li>
              <li><strong>Retail Primary Care:</strong> With Amazon's One Medical and virtual clinics like Hims & Hers scaling rapidly, the "paternalistic" model of doctor-controlled information is being replaced by 24/7 subscription-based access.</li>
            </ul>
            <p>
              The FDA is finally signal-shifting, with leadership stating the agency needs to move "at the speed of Silicon Valley." While doctors worry about over-testing and "AI hallucinations," the reality is that a third of Americans skipped care last year due to costs. For the first time, the combination of generative AI and regulatory friendliness is making the dream of a decentralized, consumer-led health system look less like a pitch deck and more like an inevitability.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              The Economist. (2026, March 10). A new wave of disrupters takes on American health care.{' '}
              <a href="https://www.economist.com/business/2026/03/10/a-new-wave-of-disrupters-takes-on-american-health-care" target="_blank" rel="noopener noreferrer">The Economist</a>.
            </p>
          </div>
          <a
            href="https://www.economist.com/business/2026/03/10/a-new-wave-of-disrupters-takes-on-american-health-care"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Economist</span>
            <span className="news-video-link-title">A new wave of disrupters takes on American health care ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'microsoft-biosecurity-toxin-screening',
      section: 'Rx',
      title: 'Microsoft Researchers Find - AI Protein Design Biosecurity Gap',
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Microsoft scientists demonstrated that open-source AI protein design (AIPD) tools can generate thousands of synthetic toxin variants — altering amino acid sequences while preserving structure and likely function — that would evade screening systems used by DNA synthesis companies. The finding, from a computer simulation study, exposed a critical blind spot in biosecurity.
            </p>
            <p>
              Over 10 months, a cross-sector team worked discreetly to develop biosecurity "red-teaming" processes and a detection patch distributed globally to DNA synthesis companies. Current screening software was inadequate at detecting "paraphrased" versions of concerning protein sequences; AI has lowered the barrier to custom protein design, accelerating both beneficial research and misuse risk.
            </p>
            <p>
              Eric Horvitz, Microsoft's chief scientific officer and project lead, frames the study as dual-use: AI in biology offers breakthroughs in cures and therapies, but requires pairing innovation with safeguards — technical fixes, regulation, and informed citizens. The peer-reviewed paper was published in <em>Science</em> on October 2.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Kubota, S. (2025, October 2). Researchers find — and help fix — a hidden biosecurity threat.
            </p>
          </div>
          <a
            href="https://news.microsoft.com/signal/articles/researchers-find-and-help-fix-a-hidden-biosecurity-threat/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Microsoft Signal</span>
            <span className="news-video-link-title">Researchers find — and help fix — a hidden biosecurity threat — Read on Microsoft Signal ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'teens-sleep-deprivation-jama',
      section: 'Rx',
      title: `Teens Are Sleeping Less Than Ever (Screens Aren't the Cause)`,
      date: 'March 8, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              A new report published in the Journal of the American Medical Association (JAMA) highlights a
              growing "epidemic" of sleep deprivation among American teenagers. Analyzing data from
              121,000 high school students via the Youth Risk Behavior Survey, researchers found that the vast
              majority of adolescents are failing to get the recommended eight hours of sleep.
            </p>
            <h3>Key Statistics</h3>
            <p>
              In 2023, roughly 75% of American adolescents reported insufficient sleep (less than 8 hours), an
              8% increase since 2007. The percentage of teens getting "very short sleep" (5 hours or less)
              swelled from 15.8% to 23.0% over the same period. While sleep loss increased across all groups
              regardless of sex or age, Black students showed a significantly greater increase in insufficient
              sleep compared to white students.
            </p>
            <h3>The Screen Time Paradox</h3>
            <p>
              Contrary to popular belief, the study found that the rise in sleep deprivation was not primarily
              driven by social media or TV use. In fact, teens with very little screen time saw a greater rise
              in insufficient sleep than heavy screen users.
            </p>
            <h3>Potential Culprits</h3>
            <p>
              Lead researcher Tanner Bommersbach suggests that earlier school start times, heavy extracurricular
              demands, and less parental monitoring are more likely contributors than electronics.
            </p>
            <h3>Expert Recommendations</h3>
            <p>
              To combat this trend, experts like Dr. Anita Shelgikar recommend consistent sleep schedules,
              morning light exposure to reset circadian rhythms, and policy changes such as delaying school
              start times to align with adolescent biological needs.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Daniel, A. (2026, March 8). Teens are sleeping less than ever and screens aren't primarily to blame.{' '}
              <a href="https://www.npr.org/2026/03/08/nx-s1-5737426/time-change-teens-sleep-screens" target="_blank" rel="noopener noreferrer">NPR: Weekend Edition Sunday</a>.
            </p>
          </div>
          <a
            href="https://www.npr.org/2026/03/08/nx-s1-5737426/time-change-teens-sleep-screens"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">NPR</span>
            <span className="news-video-link-title">Teens are sleeping less than ever and screens aren't primarily to blame — Read on NPR ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'prescription-drug-savings-2026',
      section: 'Rx',
      title: 'How to Actually Pay Less for Prescription Drugs in 2026',
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Drug costs are rising at <strong>4.3% annually</strong>, but the 2026 landscape has more
              consumer tools than ever — if you know where to look. The key is understanding which
              programs interact with your insurance and which don't.
            </p>
            <h3>The $2,100 Cap</h3>
            <p>
              Since 2025, Medicare Part D out-of-pocket spending is capped. In 2026 the limit is{' '}
              <strong>$2,100</strong> (plus premiums). Hit it early and your plan covers 100% of drug
              costs for the rest of the year — one of the most significant consumer protections in
              Medicare's history.
            </p>
            <h3>Medicare Negotiations</h3>
            <p>
              Negotiated prices are now in effect for 10 major drugs including <strong>Eliquis,
              Jardiance, Januvia, and Enbrel</strong>. Medicare enrollees are projected to save{' '}
              <strong>$1.5 billion</strong> in 2026 as a result.
            </p>
            <h3>TrumpRx — The Fine Print</h3>
            <p>
              The federal TrumpRx portal launched in February 2026 aggregates manufacturer coupons for
              ~44 high-cost brand-name drugs including Ozempic, Wegovy, and fertility treatments like
              Gonal-F. The catch: it's <strong>cash-pay only</strong>. Purchases made through TrumpRx
              do not count toward your insurance deductible or the Medicare $2,100 cap. Do the math
              before skipping your insurance.
            </p>
            <h3>Where to Actually Save</h3>
            <p>
              For generics, <strong>Cost Plus Drugs</strong> (Mark Cuban) and <strong>Amazon
              Pharmacy</strong> are almost always cheaper — a 90-day supply of many generics runs
              $10 at Walmart. Check Medicare.gov's Plan Finder annually; formularies change every year
              and a higher-premium plan can net out cheaper depending on your specific medications.
              Always confirm your pharmacy is a "preferred" partner — using the wrong one can cost
              hundreds in unnecessary co-pays.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              O'Brien, E. (2026, March 5). Prescription Drug Prices Can Break the Bank. How to Get the Best Deals.{' '}
              <a href="https://www.barrons.com/articles/retirement-drug-prices-walmart-costco-96d0dc49" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/articles/retirement-drug-prices-walmart-costco-96d0dc49"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">Prescription Drug Prices Can Break the Bank. How to Get the Best Deals — Read on Barron's ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'prasad-fda-exit',
      section: 'Rx',
      title: 'Trump Appointee Dr. Vinay Prasad to Exit FDA Following Tumultuous Tenure',
      date: 'March 6, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Dr. Vinay Prasad, the head of the FDA's vaccines and related biologics division, will depart
              the agency in April 2026 after a single year in the role. Appointed by the Trump administration
              to overhaul drug approval standards and COVID-19 vaccine frameworks, Prasad leaves behind a
              tenure defined as much by friction as reform.
            </p>
            <p>
              His time at the agency was marked by regulatory reversals for rare-disease drugs, a public
              dispute with Moderna over its flu shot application, and internal personnel complaints. FDA
              Commissioner Marty Makary credited Prasad with successfully implementing new
              efficiency-focused policies — though the praise came alongside his exit.
            </p>
            <p>
              Prasad's tenure also drew scrutiny over the unusual circumstances of his hiring: he had
              previously been dismissed, then rehired, raising questions about the administration's vetting
              process for senior regulatory appointments at one of the country's most consequential
              health agencies.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Whyte, L. E. (2026, March 6). FDA's Controversial Vaccines Chief Will Leave the Agency.{' '}
              <a href="https://www.wsj.com/health/healthcare/fdas-controversial-vaccines-chief-will-leave-the-agency-7fe59aa3" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/health/healthcare/fdas-controversial-vaccines-chief-will-leave-the-agency-7fe59aa3"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">FDA's Controversial Vaccines Chief Will Leave the Agency — Read on WSJ ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'glp1-addiction',
      section: 'Rx',
      title: 'Could Ozempic Treat Addiction?',
      date: 'March 4, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              A growing body of research suggests that GLP-1 receptor agonists — popular diabetes and
              weight-loss medications like Ozempic, Wegovy, and Mounjaro — may significantly reduce cravings
              and risks associated with substance use disorders. While these drugs are already known for
              treating obesity and cardiovascular issues, scientists believe they could become a primary
              pharmacotherapy for addiction.
            </p>
            <h3>Key Findings</h3>
            <ul>
              <li><strong>Reduced Risk and Overdose:</strong> A large-scale study of over 600,000 veterans published in the BMJ found that patients on GLP-1s were less likely to develop new substance use disorders — including alcohol, nicotine, and opioids — compared to those on other diabetes medications. Most notably, the study associated GLP-1 use with a <strong>50% reduction in drug-related deaths</strong>.</li>
              <li><strong>Neurological Impact:</strong> The drugs appear to work by "quieting" reward systems in the brain. Beyond reducing "food noise," they may dampen the biological drive for addictive substances like cocaine and cannabis — areas where few or no medical treatments currently exist.</li>
              <li><strong>Mental Health Benefits:</strong> Contrary to earlier concerns regarding suicidal ideation, the BMJ study noted a <strong>25% reduction in suicidal thoughts</strong> among users.</li>
            </ul>
            <h3>The Path Forward</h3>
            <p>
              Despite the promising data, experts caution that current evidence is largely observational or
              based on animal studies. There are concerns regarding "healthy user bias" and the lack of
              demographic diversity in VA data. Several large-scale randomized controlled trials are currently
              underway, with results for alcohol-use disorder expected within the next six months.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Tirrell, M. (2026, March 4). Could popular weight loss drugs become the new treatment for addiction? Evidence is starting to mount.{' '}
              <a href="https://www.cnn.com/2026/03/04/health/glp1-addiction-treatment" target="_blank" rel="noopener noreferrer">CNN Health</a>.
            </p>
          </div>
          <a
            href="https://www.cnn.com/2026/03/04/health/glp1-addiction-treatment"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">CNN Health</span>
            <span className="news-video-link-title">Could popular weight loss drugs become the new treatment for addiction? — Read on CNN ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'infant-gut-health',
      section: 'Rx',
      title: 'The New Frontier of Baby Wellness: Infant Gut Health',
      date: 'March 2026',
      body: () => (
        <div className="news-article-body">
          <p>
            A fast-growing market is emerging as parents and startups turn their attention to the infant microbiome—the
            complex community of bacteria in a baby's digestive tract. While some scientists believe this early window
            is critical for long-term health, pediatricians urge caution as marketing often outpaces proven science.
          </p>
          <h3>The Rise of "Baby-Biotics"</h3>
          <ul>
            <li><strong>Direct-to-Consumer Testing:</strong> Companies like Tiny Health ($249) and Alba Health ($349) analyze infant stool samples to identify "unfriendly" vs. "beneficial" bacteria.</li>
            <li><strong>Custom Recommendations:</strong> Beyond supplements, these services suggest lifestyle changes to diversify a baby's microbiome, such as playing in dirt piles, visiting petting zoos, or walking barefoot.</li>
            <li><strong>Market Growth:</strong> Sales for children's digestive supplements reached approximately $59 million in a recent 52-week period. Probiotic-infused baby food is now outselling products marketed for brain or immune support.</li>
          </ul>
          <h3>What the Science Says</h3>
          <ul>
            <li><strong>The "Malleable" Window:</strong> The microbiome is set up early in life, and dysfunction during this period is linked to adult risks for Type 2 diabetes, obesity, and heart disease.</li>
            <li><strong>Industrialized Changes:</strong> Modern factors like C-section births and the overuse of antibiotics are changing baby microbiomes in ways that may cause low-level inflammation.</li>
            <li><strong>Key Findings:</strong> A 2025 study in The Lancet Microbe found that specific bacterial compositions in one-week-old babies (typically from vaginal births) were linked to fewer respiratory hospitalizations before age 2.</li>
          </ul>
          <h3>Expert Caution</h3>
          <ul>
            <li><strong>Defining "Good" vs. "Bad":</strong> Dr. Mark Corkins (American Academy of Pediatrics) warns that it is currently difficult to define exactly what constitutes a "good" microbiome, making some testing premature.</li>
            <li><strong>Safety Warnings:</strong> The FDA issued a 2023 warning regarding the risks of probiotics for hospitalized premature infants, noting they could cause fatal infections.</li>
          </ul>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Petersen, A. Babies' Gut Health Is the New Obsession for Parents—and Startups.{' '}
            <a href="https://www.wsj.com/health" target="_blank" rel="noopener noreferrer">Wall Street Journal</a>.
          </p>
        </div>
      ),
    },
  ],

  fish: [
    {
      slug: 'nvidia-blackwell-beast-gpu',
      section: 'Fish',
      title: 'Nvidia Unveils the Blackwell Beast',
      date: 'March 18, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> Jensen Huang drops the Blackwell GPU, a "massive" AI engine designed to keep Nvidia's trillion-dollar momentum in overdrive.
            </p>
            <p>
              Nvidia isn't just leading the AI race; it's rewriting the physics of the track. At the highly anticipated GTC event, CEO Jensen Huang unveiled the "Blackwell" architecture, a processing powerhouse designed to handle the trillion-parameter models that define the next frontier of Generative AI. This isn't a mere incremental update—it's a declaration of total market dominance aimed squarely at competitors trying to chip away at Nvidia's 80% market share.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Powerhouse:</strong> The new Blackwell B200 GPU packs 208 billion transistors, delivering up to 20 petaflops of FP4 horsepower—making it significantly faster and more efficient than the previous H100 "Hopper" chips.</li>
              <li><strong>The Ecosystem:</strong> Nvidia isn't just selling chips; it's selling the GB200 NVL72, a liquid-cooled rack system that acts as a single, massive GPU, designed to scale for the world's largest data centers.</li>
              <li><strong>The Software Moat:</strong> The launch of Nvidia NIM (Nvidia Inference Microservices) simplifies how developers deploy AI, effectively "packaging" models to run seamlessly across Nvidia's massive installed base of hardware.</li>
              <li><strong>The Partnerships:</strong> Tech titans including Amazon, Google, Microsoft, and Oracle have already lined up as the first customers, signaling that the "CapEx arms race" among hyperscalers shows no signs of cooling down.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> Nvidia is successfully transitioning from a hardware vendor to a full-stack "AI foundry" for the global economy. By drastically reducing the cost and energy consumption of training massive models, Blackwell makes the "AI Industrial Revolution" economically viable for more than just the top 1% of tech firms. For investors, the focus shifts from "can they build it?" to "how high is the ceiling?" as Nvidia cements itself as the indispensable utility of the modern era.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Nvidia GTC Event: AI Chips, Stock Price, and News.{' '}
              <a href="https://www.barrons.com/livecoverage/nvidia-gtc-event-ai-chips-stock-price-news" target="_blank" rel="noopener noreferrer">Barron's</a>, March 18, 2026.
            </p>
          </div>
          <a
            href="https://www.barrons.com/livecoverage/nvidia-gtc-event-ai-chips-stock-price-news"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">Nvidia GTC Event: AI Chips, Stock Price, and News ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'great-migration-wall-street-talent-drain-asia-corp',
      section: 'Fish',
      title: 'The Great Migration: Wall Street\'s Talent Drain to Asia Corp',
      date: 'March 17, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> Bankers are trading their pinstripes for corporate keys as Asia's volatile bonus culture triggers a mass C-suite exodus.
            </p>
            <p>
              The era of the "lifer" investment banker is hitting a regional wall. Senior dealmakers across Asia are aggressively jumping ship, abandoning the prestige of Wall Street giants like JPMorgan and Citi for stable, internal strategy roles within the very corporations they once advised. This isn't just a post-bonus shuffle; it's a strategic retreat to safety as the financial sector's boom-and-bust cycle becomes too exhausting for even the most seasoned veterans.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Pivot:</strong> High-ranking MDs and Executive Directors from JPMorgan, Citi, and Morgan Stanley are leading the charge, transitioning into technology, healthcare, and telecom sectors to escape the "high-pressure cooker" of investment banking.</li>
              <li><strong>The Roster:</strong> Heavy hitters on the move include Virginia Zhang (JPMorgan MD), Horace Liang (Citi MD), and Kenneth Sun (Morgan Stanley's Healthcare Head), who is set to lead regional business for Royalty Pharma.</li>
              <li><strong>The Incentive:</strong> While banking offers high ceilings, corporate roles provide locked-in compensation packages and immunity from the cyclical "ebbs and flows" of equity capital markets and volatile bonus pools.</li>
              <li><strong>The Corporate Gain:</strong> Firms like Singapore Telecommunications and various tech unicorns are the primary beneficiaries, successfully poaching "banking brain trust" to bolster their internal M&A and finance strategies.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This shift signals a maturing Asian corporate landscape where internal strategy teams are now sophisticated enough to compete for—and win—top-tier global talent. For Wall Street, this is a double-edged sword: they are losing their most connected rainmakers just as regional dealmaking activity begins to rebound. If banks can't fix their "burnout-to-bonus" ratio, they risk becoming mere training grounds for the next generation of corporate CFOs.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Senior bankers quit Wall Street jobs for corporate roles in Asia.{' '}
              <a href="https://www.bloomberg.com/news/articles/2026-03-17/senior-bankers-quit-wall-street-jobs-for-corporate-roles-in-asia" target="_blank" rel="noopener noreferrer">The Business Times / Bloomberg</a>, March 17, 2026.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/articles/2026-03-17/senior-bankers-quit-wall-street-jobs-for-corporate-roles-in-asia"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg</span>
            <span className="news-video-link-title">Senior bankers quit Wall Street jobs for corporate roles in Asia ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'morgan-stanley-private-credit-gate-barron',
      section: 'Fish',
      title: 'Second Bank to Limit Private Credit Withdrawal',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <Link to="/paper/fish/blackrock-hlend-gate">BlackRock gated its $26B HPS Corporate Lending Fund last week</Link>. Now Morgan Stanley's North Haven Private Income Fund has slapped the gate down, limiting redemptions after investors tried to pull out double the allowed quarterly limit. This isn't an isolated tremor — it's a systemic shudder. With giants like Blue Owl and BlackRock already defensive, the "shadow banking" sector is learning a hard lesson: private loans are easy to ink but incredibly difficult to exit when the macro weather turns.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Redemption Gap:</strong> Morgan Stanley received buyback requests for 10.9% of shares, but strictly enforced its 5% cap, leaving half of the exit-seeking capital trapped in the fund.</li>
              <li><strong>The Sector Contagion:</strong> Blue Owl (-3.2%) and BlackRock (-3.2%) are already under fire, while Ares Management took a massive 6.7% hit as the market re-prices the risk of private debt.</li>
              <li><strong>The Valuation Reset:</strong> The VanEck Alternative Asset Manager ETF has slid 23% in 2026 as the "higher for longer" rate environment begins to rot underlying loan books.</li>
              <li><strong>The Credit Squeeze:</strong> JPMorgan is reportedly tightening the screws, marking down software loans and restricting leverage to these funds — cutting off the oxygen that fueled the private credit boom.</li>
              <li><strong>The Bear Case:</strong> Managers cite "contraction in asset yields" and "speculation on credit deterioration" — borrowers are struggling to pay and there are no buyers for the debt.</li>
            </ul>
            <p>
              Private credit was pitched as the high-yield, low-volatility savior for institutional portfolios, but we're witnessing its first real liquidity stress test. When "permanent capital" vehicles hit withdrawal gates, fear leads to more redemption requests, which leads to more gates, which leads to a collapse in the stock prices of the managers themselves. If the $1.7T private credit market cracks, the spillover won't just hit Wall Street — it will starve the mid-market companies that rely on these funds for survival.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Wolf, N. (2026, March 12). Morgan Stanley Private Credit Fund Caps Withdrawals. More Trouble for Blue Owl Stock, Ares.{' '}
              <a href="https://www.barrons.com/articles/morgan-stanley-private-credit-stock-price-blue-owl-3e6f69bb?mod=hp_LEDE_C_3" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/articles/morgan-stanley-private-credit-stock-price-blue-owl-3e6f69bb?mod=hp_LEDE_C_3"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">Morgan Stanley Private Credit Fund Caps Withdrawals — Read on Barron's ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'markets-bleed-crude-100-hormuz-barron',
      section: 'Fish',
      title: 'Markets Bleed as Crude Crosses $100',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The bulls are in full retreat as a geopolitical nightmare becomes a fiscal reality. With the Strait of Hormuz effectively shuttered and the U.S. military signaling a lack of immediate tanker escorts, Brent crude just pulled its biggest one-day dollar gain in four years. Investors are no longer just pricing in war — they are pricing in a stagflationary trap that leaves the Federal Reserve with zero room to maneuver.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Price Action:</strong> The Dow plunged 731 points (1.5%), while the Nasdaq bore the brunt of the sell-off with a 1.8% slide, as tech valuations buckled under rising yields.</li>
              <li><strong>The Energy Spike:</strong> Brent crude surged 9.2% to $100.46, clearing the psychological century mark. The largest percentage gain since the 2020 pandemic recovery.</li>
              <li><strong>The Policy Pivot:</strong> The "Fed Pivot" is officially on life support. CME FedWatch Tool data shows odds of no rate cuts in 2026 skyrocketed to 46.1%, up from just 5.1% a month ago.</li>
              <li><strong>The Technical Warning:</strong> Breadth is deteriorating rapidly. Analysts are flagging a potential "trap door" scenario for the S&P 500, with some technical targets dropping as low as 6000.</li>
              <li><strong>The Data Fog:</strong> Friday's PCE inflation report — usually a market-mover — is being dismissed as "stale" due to the partial government shutdown, leaving investors flying blind through the volatility.</li>
            </ul>
            <p>
              This isn't just a bad day on the floor — it's a structural shift in the 2026 economic thesis. $100 oil acts as an immediate tax on the global consumer and a massive inflationary tailwind that effectively handcuffs the Federal Reserve. If the Strait of Hormuz remains a no-go zone, the transition from "soft landing" to "stagflationary freeze" moves from a tail risk to the base case. The era of cheap energy and easy money is being held hostage by maritime geography.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Smith, C. (2026, March 12). Dow Tumbles on Oil Prices Ahead of Friday's Inflation Report.{' '}
              <a href="https://www.barrons.com/livecoverage/stock-market-news-today-031226?mod=hp_LEDE_C_1" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/livecoverage/stock-market-news-today-031226?mod=hp_LEDE_C_1"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">Dow Tumbles on Oil Prices Ahead of Friday's Inflation Report ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'pokemon-go-delivery-robots-niantic-vps',
      section: 'Fish',
      title: 'From Augmented Reality to Autonomous Delivery',
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Niantic Spatial, an AI spinout from the creators of Pokémon Go, is repurposing a massive dataset of 30 billion images crowdsourced from over 100 million players to solve the "urban canyon" problem in robotics. While standard GPS can drift by up to 50 meters in dense cities due to signal interference, Niantic's new "world model" uses a Visual Positioning System (VPS) to provide location accuracy within centimeters.
            </p>
            <p>
              The technology is being integrated into Coco Robotics' fleet of 1,000 delivery robots, which have already completed 500,000 deliveries. By recognizing landmarks from player-captured data, these robots can now navigate precisely to restaurant pickup spots and customer doors without getting lost. This shift marks a transition where AR data is no longer just for gamers, but is becoming the essential "connective tissue" for machines to navigate the physical world.
            </p>
            <h3>Key Data Points</h3>
            <ul>
              <li><strong>Dataset Size:</strong> 30 billion images / 1 million+ precise locations.</li>
              <li><strong>Precision:</strong> Centimeter-level accuracy vs. 50-meter GPS drift.</li>
              <li><strong>Scale:</strong> 1,000 robots; 500,000+ deliveries to date.</li>
            </ul>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Heaven, W. D. (2026, March 10). How Pokémon Go is giving delivery robots an inch-perfect view of the world.{' '}
              <a href="https://www.technologyreview.com/2026/03/10/1134099/how-pokemon-go-is-helping-robots-deliver-pizza-on-time/" target="_blank" rel="noopener noreferrer">MIT Technology Review</a>.
            </p>
          </div>
          <a
            href="https://www.technologyreview.com/2026/03/10/1134099/how-pokemon-go-is-helping-robots-deliver-pizza-on-time/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">MIT Technology Review</span>
            <span className="news-video-link-title">How Pokémon Go is giving delivery robots an inch-perfect view of the world ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'ge-vernova-vietnam-lng-200m',
      section: 'Fish',
      title: "GE Vernova's $200M Infrastructure Play in Vietnam",
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              GE Vernova has announced a $200 million investment to build a new high-voltage transformer facility in Hai Phong, Vietnam. This move is part of a broader $11 billion capital push (2025–2028) to meet skyrocketing global demand for electrification. Vietnam is currently in a "decisive execution phase" of its energy transition, aiming to reach 22.5 gigawatts of LNG-fired capacity by 2030 — a significant jump from the 1.62 GW currently online.
            </p>
            <p>
              GE's high-efficiency 9HA.02 gas turbines and generators will power three major projects (Quynh Lap, Hai Phong, and Quang Trach II), each capable of 1.6 GW. The expansion comes as GE reports a massive $2 billion in orders tied specifically to data centers and a total gas-power backlog of 83 GW. The new plant is expected to create 450 jobs and position Vietnam as a critical hub in the global power equipment supply chain.
            </p>
            <h3>Key Data Points</h3>
            <ul>
              <li><strong>Investment:</strong> $200 million for the Hai Phong facility.</li>
              <li><strong>Capacity Targets:</strong> Vietnam's goal of 22.5 GW LNG capacity by 2030.</li>
              <li><strong>Global Demand:</strong> GE gas-power backlog increased from 62 GW to 83 GW.</li>
            </ul>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Saeed, K. (2026, March 10). GE Vernova to Invest $200 Million in Vietnam as LNG, Grid Buildout Accelerates.{' '}
              <a href="https://ts2.tech/en/ge-vernova-to-invest-200-million-in-vietnam-as-lng-grid-buildout-accelerates/" target="_blank" rel="noopener noreferrer">TS2 Tech</a>.
            </p>
          </div>
          <a
            href="https://ts2.tech/en/ge-vernova-to-invest-200-million-in-vietnam-as-lng-grid-buildout-accelerates/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">TS2 Tech</span>
            <span className="news-video-link-title">GE Vernova to Invest $200 Million in Vietnam as LNG, Grid Buildout Accelerates ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'stocks-surge-trump-iran-war-complete',
      section: 'Fish',
      title: 'Stocks Surge After Trump Says Iran War Is "Very Complete"',
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              U.S. stocks staged their largest intraday reversal in nearly a year on March 9, erasing sharp early losses after President Trump suggested to CBS's Weijia Jiang that the Iran conflict could be over soon. "I think the war is very complete, pretty much," Trump said. "They have no navy, no communications, they've got no Air Force." He added the U.S. was "very far" ahead of the four-to-five week timeline the administration had previously offered.
            </p>
            <p>
              At their worst, the Dow was down nearly 1.9% and the S&P 500 and Nasdaq each down about 1.5% as oil neared $120 a barrel. The G-7's signal that it could release strategic reserves helped cool prices. By the close, the Dow gained 239 points (0.5%), the S&P 500 rallied 0.8%, and the Nasdaq rose 1.4%. WTI crude slid 5.1% to $86.49; Brent fell 9% to $90.10.
            </p>
            <p>
              Tech led the rebound, with chip stocks — Nvidia, Broadcom, KLA — outperforming. The reversal marked the S&P and Dow's biggest swing from intraday low to positive since April 30, 2025, according to Dow Jones Market Data.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Smith, C. (2026, March 9). Stock market news today: Stocks surge after Trump says Iran war is "very complete".{' '}
              <a href="https://www.barrons.com/livecoverage/stock-market-news-today-030926" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/livecoverage/stock-market-news-today-030926"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">Stock market news today: Stocks surge after Trump says Iran war is "very complete" — Read on Barron's ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'kalshi-polymarket-20b-valuations',
      section: 'Fish',
      title: 'Kalshi and Polymarket Eyeing $20 Billion Valuations',
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Rival prediction markets Kalshi and Polymarket are both reportedly in early-stage discussions
              with investors to raise capital at valuations of approximately $20 billion. This would roughly
              double their respective valuations from late 2024, reflecting the explosive growth and increased
              mainstream adoption of betting on real-world events.
            </p>
            <h3>Rapid Growth and Revenue</h3>
            <p>
              <strong>Kalshi:</strong> Already operational and regulated in the U.S., Kalshi has reportedly
              crossed a revenue run rate of $1 billion, with some estimates placing it as high as $1.5 billion.
              It was last valued at $11 billion in December 2024.
            </p>
            <p>
              <strong>Polymarket:</strong> While currently off-limits to U.S. users (though accessible via VPN),
              Polymarket was valued at $9 billion in October 2024 following an investment from the Intercontinental
              Exchange. It plans to launch a domestically regulated U.S. app later this year.
            </p>
            <h3>Controversial Markets</h3>
            <p>
              Both platforms have faced intense scrutiny for hosting "war markets," allowing users to bet on
              the U.S. striking Iran and the potential ouster of Ayatollah Ali Khamenei.
            </p>
            <h3>Legislative Pushback</h3>
            <p>
              On March 6, 2026, U.S. Representatives introduced legislation to restrict these platforms from
              offering markets on sensitive topics, including war and sports.
            </p>
            <h3>Aggressive Marketing</h3>
            <p>
              The platforms have been criticized for aggressive outreach to college students. Reports indicate
              Polymarket has paid fraternities thousands of dollars for new user sign-ups, and regulators are
              looking into "questionable trades," such as bets placed on Jeff Bezos' location during the
              Super Bowl by members of his stepson's fraternity.
            </p>
            <p>
              Despite significant regulatory and ethical headwinds, the massive valuations sought by Kalshi and
              Polymarket signal that investors see prediction markets as a major new pillar of the financial and
              entertainment sectors.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Clark, K., &amp; Dugan, K. T. (2026, March 7). Kalshi and Polymarket are each eyeing roughly $20 billion valuations.{' '}
              <a href="https://www.wsj.com/finance/kalshi-and-polymarket-are-each-eyeing-roughly-20-billion-valuations-d7b9c5d8" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/finance/kalshi-and-polymarket-are-each-eyeing-roughly-20-billion-valuations-d7b9c5d8"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">Kalshi and Polymarket are each eyeing roughly $20 billion valuations — Read on WSJ ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'post-mag7',
      section: 'Fish',
      title: 'The Post-Mag 7 Era',
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
          <p>
            The <strong>"Magnificent Seven"</strong> — Alphabet, Amazon, Apple, Meta, Nvidia, Microsoft,
            and Tesla — have officially lost their market-dominating crown. After a staggering 76% gain
            in 2023 and 47.5% in 2024, the group is <strong>down 7.2% year-to-date</strong> in early
            2026. While they still represent one-third of the S&amp;P 500's market cap, the
            "set-it-and-forget-it" era has fizzled.
          </p>
          <h3>Why the "Slam-Dunk" Trade Died</h3>
          <p>
            <strong>The Capex Crush:</strong> Tech hyperscalers are projected to spend a jaw-dropping{' '}
            <strong>$670 billion</strong> on data centers and Nvidia chips in 2026. This massive spending
            is eating profit margins and turning free cash flow negative for giants like Oracle.
          </p>
          <p>
            <strong>The AI Equalizer:</strong> Strategy expert Peter Berezin argues AI is a
            "moat-killer." It lowers the cost of entry for small competitors, dilutes social networks
            with bot-driven slop, and favors open-source tech over proprietary monopolies.
          </p>
          <p>
            <strong>Mean Reversion:</strong> While the Mag 7 stumbles, the Invesco S&amp;P 500 Equal
            Weight ETF is <strong>up 5.1%</strong> this year — the "other 493" stocks are finally
            catching a breeze.
          </p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Serwer, A. (2026, March 6). The Mag 7 Are Dead. How the Hottest Trade of the Decade Fizzled Out.{' '}
            <a href="https://www.barrons.com/articles/mag-7-stocks-dead-nvidia-facebook-amazon-google-03726dbb?st=F4yTde" target="_blank" rel="noopener noreferrer">Barron's</a>.
            Data via FactSet and BCA Research.
          </p>
        </div>
          <a
            href="https://www.barrons.com/articles/mag-7-stocks-dead-nvidia-facebook-amazon-google-03726dbb?st=F4yTde"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">The Mag 7 Are Dead. How the Hottest Trade of the Decade Fizzled Out — Read on Barron's ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'blackrock-hlend-gate',
      section: 'Fish',
      title: 'BlackRock Gates Its $26B Private Credit Fund as Withdrawals Double the Limit',
      date: 'March 6, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              BlackRock has restricted redemptions for its $26 billion flagship private credit fund — the
              HPS Corporate Lending Fund (HLEND) — after withdrawal requests reached 9.3%, nearly double the
              fund's 5% quarterly limit. The restriction, known as a "gate," prevents the fund from becoming
              a forced seller of assets during a period of heightened volatility.
            </p>
            <p>
              The move has amplified investor anxiety across the $1.8 trillion private credit industry,
              where questions over asset valuations and the downstream impact of AI on borrowers have been
              building for months. Shares of BlackRock and other major alternative asset managers fell
              on the news.
            </p>
            <p>
              Analysts framed the gate as a structural protection rather than a distress signal — a feature
              of the fund's design rather than evidence of underlying credit problems. Still, the scale of
              redemption pressure is a notable stress test for an asset class that has expanded rapidly
              since rate hikes began in 2022.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Brush, S., &amp; Fishlow, O. (2026, March 6). BlackRock $26 Billion Private Credit Fund Limits Withdrawals.{' '}
              <a href="https://www.bloomberg.com/news/articles/2026-03-06/blackrock-s-26-billion-private-credit-fund-limits-withdrawals" target="_blank" rel="noopener noreferrer">Bloomberg</a>.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/articles/2026-03-06/blackrock-s-26-billion-private-credit-fund-limits-withdrawals"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg</span>
            <span className="news-video-link-title">BlackRock $26 Billion Private Credit Fund Limits Withdrawals — Read on Bloomberg ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'costco-slump',
      section: 'Fish',
      title: 'Retail Giant Slumped as Holiday Inflation Fears Take Hold',
      date: 'December 15, 2025',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Costco Wholesale (COST) shares felt the weight of a shifting economic landscape this Monday.
              Despite the traditional holiday rush, a rare "Strong Sell" signal from Roth Capital sent the
              stock tumbling 3.6% as investors grappled with the reality of "sticky" inflation and a cooling
              retail environment.
            </p>
            <h3>Key Highlights from the Analyst Report</h3>
            <ul>
              <li><strong>The Rare Downgrade:</strong> Roth Capital didn't just trim expectations; they issued a double-downgrade from Neutral to Sell, slashing the price target to $769.</li>
              <li><strong>Decelerating Momentum:</strong> Analyst Bill Kirk highlighted "concerning" drops in same-store sales growth and a slowdown in new member sign-ups.</li>
              <li><strong>The Renewal Rift:</strong> While Costco's global renewal rate remains high (~90%), Roth warned that domestic renewals are "fading," potentially due to the saturated U.S. market and increased competition from Walmart (WMT).</li>
              <li><strong>The Valuation Gap:</strong> At a P/E ratio over 45x, critics argue the stock was "priced to perfection," leaving no room for the slight misses reported in the latest quarterly update.</li>
            </ul>
            <h3>The Market Reaction</h3>
            <p>
              The ripple effect of the Costco slide was felt across the "defensive" retail sector, as traders
              moved away from high-valuation staples:
            </p>
            <ul>
              <li><strong>Costco Wholesale (COST):</strong> ↓ 3.6% ($860.57)</li>
              <li><strong>BJ's Wholesale (BJ):</strong> ↓ 1.5%</li>
              <li><strong>Consumer Staples ETF (XLP):</strong> Dragged lower by heavy-weight retail losses.</li>
            </ul>
            <h3>The Bottom Line: The Inflation Factor</h3>
            <p>
              The "Bottom Line" isn't just about the stock price — it's about the 2.7% annual inflation rate
              (CPI) that defined December 2025. While headline inflation has cooled from its peaks, "sticky"
              prices in food (up 3.1%) and services have created a bifurcated holiday season.
            </p>
          </div>
          <a
            href="https://www.fool.com"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Motley Fool</span>
            <span className="news-video-link-title">Market Sentiment: Inflation Pressures Shift Consumer Behavior — Read on Motley Fool ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'south-korea-kospi-crash',
      section: 'Fish',
      title: "South Korea's Stock Market Hits Historic Low amid US-Iran War",
      date: 'March 2026',
      body: () => (
        <div className="news-article-body">
          <p>
            South Korea just witnessed the steepest single-day drop in its stock market history. Driven by the escalating
            conflict between the U.S., Israel, and Iran, the benchmark KOSPI index plummeted 12.06% on Wednesday. This
            crash surpassed the previous record set during the September 11 attacks in 2001.
          </p>
          <h3>Key Takeaways</h3>
          <ul>
            <li><strong>Historic Losses:</strong> The 12.06% drop eclipsed the 12.02% single-day plunge from 2001. This followed a 7.2% fall on Tuesday, marking the worst two-day streak in decades.</li>
            <li><strong>Market Safeguards:</strong> Financial authorities were forced to trigger a 20-minute circuit breaker after losses exceeded the 8% threshold.</li>
            <li><strong>Corporate Impact:</strong> Blue-chip giants like Samsung Electronics, SK Hynix, and LG Electronics all saw significant hits.</li>
            <li><strong>Logistics Crisis:</strong> Shipping firms like Pan Ocean and HMM plunged up to 17% due to the halt of traffic through the Strait of Hormuz, a vital artery for 20% of global oil.</li>
            <li><strong>Energy Vulnerability:</strong> The crash underscores South Korea's reliance on foreign energy, as the nation imports roughly 98% of its fossil fuel needs.</li>
          </ul>
          <p>
            This meltdown serves as a stark reminder of how geopolitical instability in the Middle East can trigger
            immediate global economic shocks.
          </p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Power, J. South Korea stock market meltdown.{' '}
            <a href="https://finance.yahoo.com/news/south-korea-stock-market-meltdown-061927953.html" target="_blank" rel="noopener noreferrer">Yahoo Finance</a>.
          </p>
        </div>
      ),
    },
    {
      slug: 'hedge-fund-5-trillion',
      section: 'Fish',
      title: 'Global Hedge Fund Industry Capital Surges Past Historic $5 Trillion Milestone',
      date: 'January 22, 2026',
      body: () => (
        <div className="news-article-body">
          <p>Total global hedge fund industry capital surged above the historic $5 trillion milestone for the first time, with capital jumping for the 9th consecutive quarter in Q4 2025 to end the year at a record $5.15 trillion, as reported in the latest <em>HFR Global Hedge Fund Industry Report</em>.</p>
          <p>Hedge fund managers navigated a volatile Q4 with risk sentiment oscillating between powerful risk-on and extreme risk-off sentiment across thematic micro-cycles, strategies and regional markets.</p>
          <p>Industry capital rose by $178.9 billion in Q4, driven by strong performance-based gains of $134.1 billion and quarterly net asset inflows of $44.8 billion. For the full year 2025, total industry capital grew by a record $642.8 billion — inclusive of performance-based gains of $527.0 billion and net asset inflows of $115.8 billion — the strongest calendar year of investor inflows since 2007.</p>
          <p>Hedge funds also posted the strongest calendar year of performance since 2009, with the HFRI Fund Weighted Composite Index advancing +12.5 percent in 2025. Strategy performance was led by the HFRI Equity Hedge (Total) Index at +17.1 percent and the HFRI Event-Driven (Total) Index at +10.9 percent.</p>
          <p>Equity Hedge strategies grew by $72.3 billion in Q4, inclusive of net asset inflows of $20.5 billion, bringing total EH capital to $1.57 trillion. Event-Driven strategies added $42.2 billion in Q4 to bring total ED capital to $1.45 trillion. Relative Value Arbitrage strategies ended the year at $1.35 trillion, while Macro strategy assets reached $786.6 billion.</p>
          <p>Investor allocations were concentrated in the industry's largest firms. Managers with over $5 billion in AUM saw $39.3 billion in quarterly net inflows, while mid-sized firms ($1–5 billion AUM) were allocated a net $4.0 billion, and smaller firms (under $1 billion AUM) added $1.5 billion.</p>
          <blockquote className="news-article-quote">
            "Global hedge fund capital surged to surpass the historic $5 trillion milestone (by a wide margin) in the fourth quarter, driven by strong industry-wide performance and asset inflows from a wide range of investors including institutions, individuals, pensions, family offices and sovereign wealth funds."
            <cite>— Kenneth J. Heinz, President of HFR</cite>
          </blockquote>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            <a href="https://www.hfr.com/media/market-commentary/global-hedge-fund-industry-capital-surges-past-historic-5-trillion-milestone/" target="_blank" rel="noopener noreferrer">HFR Global Hedge Fund Industry Report</a>.
          </p>
        </div>
      ),
    },
  ],

  '24': [
    {
      slug: 'venezuela-wbc-final-italy',
      section: '24',
      title: 'Venezuela Books Ticket to the WBC Final',
      date: 'March 16, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> The "Espresso Run" is over as Venezuela stages a late-inning heist against Italy to set up a dream championship showdown with Team USA.
            </p>
            <p>
              Miami's loanDepot Park turned into a miniature Caracas on Monday night. Trailing 2–0 and staring down a stunning exit at the hands of tournament darlings Team Italy, Venezuela unleashed a two-out clinic in the seventh inning to flip the script. The 4–2 victory marks Venezuela's first-ever trip to the World Baseball Classic championship, officially ending the most improbable underdog run in the history of the tournament and setting the stage for a heavyweight final against the defending stars and stripes.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Rally:</strong> Down to their final nine outs, Venezuela ignited a three-run seventh inning behind four consecutive two-out hits, proving that their "never-say-die" identity from the quarterfinals wasn't a fluke.</li>
              <li><strong>The Catalysts:</strong> All-Stars Ronald Acuña Jr., Maikel Garcia, and Luis Arraez delivered back-to-back-to-back RBI singles, ruthlessly exposing the Italian bullpen after starter Aaron Nola kept them quiet through four.</li>
              <li><strong>The Shutdown:</strong> After a rocky start by Keider Montero, the Venezuelan bullpen combined for 7.2 innings of one-run ball, capped by a dominant 1-2-3 ninth from closer Daniel Palencia to silence the Italian dugout.</li>
              <li><strong>The Matchup:</strong> With the win, Venezuela (5-1) advances to face Team USA in Tuesday's final. Despite both teams sharing identical records, a coin toss has designated the U.S. as the home team for the championship.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This is more than a tournament final; it is the culmination of a decade-long quest for Venezuelan baseball legitimacy on the global stage. By knocking out both the defending champions (Japan) and the tournament's hottest underdog (Italy) in back-to-back games, Venezuela has proven they possess the clutch DNA required to win a short-series format. For the WBC, a USA vs. Venezuela final in Miami is the "gold standard" for engagement—a high-voltage clash of MLB superstars that guarantees a sellout crowd and massive international broadcast numbers.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Venezuela rallies past Italy 4-2 with 3-run 7th and will face US for World Baseball Classic title.{' '}
              <a href="https://www.nytimes.com/athletic/7123965/2026/03/16/venezuela-team-usa-wbc-final-italy/" target="_blank" rel="noopener noreferrer">The Athletic / NYT</a>, March 16, 2026.
            </p>
          </div>
          <a
            href="https://www.nytimes.com/athletic/7123965/2026/03/16/venezuela-team-usa-wbc-final-italy/"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Athletic / NYT</span>
            <span className="news-video-link-title">Venezuela rallies past Italy 4-2, will face US for WBC title ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'pga-tour-structural-overhaul-signature-events-match-play',
      section: '24',
      title: 'PGA Tour Prepares Its Biggest Structural Overhaul in a Generation',
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Under the leadership of new CEO Brian Rolapp and a committee chaired by Tiger Woods, the PGA Tour is preparing for its most significant structural shift in a generation. The overhaul aims to solve the "patchwork" nature of the current schedule by creating a clearer hierarchy of events. By doubling the number of "Signature Events" to 16 and introducing a system of promotion and relegation — similar to European soccer — the Tour intends to ensure that the world's top-ranked players face off more consistently for higher stakes. Additionally, the Tour is weighing a move to a match-play format for the postseason to eliminate complex points systems and maximize fan engagement through head-to-head drama.
            </p>
            <h3>Key Highlights</h3>
            <ul>
              <li><strong>Elite Concentration:</strong> The number of "Signature Events" (high-purse, elite-field tournaments) will double from 8 to 16.</li>
              <li><strong>Tiered Hierarchy:</strong> A new promotion and relegation system will create a clear bridge between developmental circuits and premier Tour events.</li>
              <li><strong>Postseason Overhaul:</strong> The Tour is considering replacing the current stroke-play playoff system with a Match Play format to simplify the path to the championship.</li>
              <li><strong>Market Expansion:</strong> New venues in major markets, specifically New York, are being scouted for revamped top-tier events.</li>
              <li><strong>Streamlined Fields:</strong> Tournaments will move toward ~120 players with a cut, away from traditional 144-player fields for higher quality.</li>
            </ul>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Beaton, A. (2026, March 12). Smaller Fields, Bigger Purses, More Match Play: The PGA Tour Is Changing Golf.{' '}
              <a href="https://www.wsj.com/sports/golf/pga-tour-scheffler-mcilroy-3faa3799?mod=sports_lead_story" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/sports/golf/pga-tour-scheffler-mcilroy-3faa3799?mod=sports_lead_story"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">Smaller Fields, Bigger Purses, More Match Play: The PGA Tour Is Changing Golf ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'cal-raleigh-arozarena-wbc-snub',
      section: '24',
      title: `Cal Raleigh Plays Damage Control After "Gritty" Snub of Mariners Teammate Randy Arozarena`,
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The 2026 World Baseball Classic took a cinematic — if not slightly awkward — turn in Houston this week, proving that national pride can sometimes trump clubhouse chemistry. During Monday night's high-stakes clash between Team USA and Team Mexico, Seattle Mariners catcher Cal Raleigh opted for "grit" over camaraderie, sparking a viral controversy with teammate Randy Arozarena.
            </p>
            <h3>The Snub Heard 'Round the Diamond</h3>
            <p>
              In an effort to set a competitive tone for Team USA, Raleigh — affectionately known as "The Big Dumper" — refused a routine pre-at-bat fist bump from Arozarena. The snub left the Mexico star frozen at the plate and visibly frustrated. For Arozarena, the move wasn't just gamesmanship; it was a violation of the professional code shared by teammates.
            </p>
            <p>
              The fallout was immediate. Following the game, a heated Arozarena didn't mince words with reporters, stating in Spanish that Raleigh could "go to hell" and even suggesting the catcher should "shove it up his a." In a bizarrely polite twist, Arozarena offered a backhanded compliment to Raleigh's parents, whom he had hugged at the hotel, calling them "well-educated" despite their son's lack of manners on the field.
            </p>
            <h3>Damage Control in the Clubhouse</h3>
            <p>
              By Tuesday, the "edge" Raleigh tried to bring to the tournament seemed to have overcooked. Recognizing the potential for a very tense Mariners Opening Day, Raleigh took to the airwaves with Seattle Sports 710's Shannon Drayer to douse the flames.
            </p>
            <p>
              "There's no beef. I love Randy. When we are back in Seattle he's my brother, he's family, we are all in it together," Raleigh insisted. "I don't see this being a thing at all."
            </p>
            <h3>The Bottom Line</h3>
            <p>
              While Raleigh attempted to play the role of the gritty leader, the stunt largely left him on an island. With the MLB season just weeks away, the Mariners' front office is likely hoping this "family" dispute is truly settled before the two stars have to share a dugout in Seattle.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Avila, A. (2026, March 10). Cal Raleigh Claims 'No Beef' With Randy Arozarena Despite Vulgar World Baseball Classic Rant.{' '}
              <a href="https://www.outkick.com/sports/cal-raleigh-claims-no-beef-randy-arozarena-despite-vulgar-world-baseball-classic-rant" target="_blank" rel="noopener noreferrer">OutKick</a>.
            </p>
          </div>
          <a
            href="https://www.outkick.com/sports/cal-raleigh-claims-no-beef-randy-arozarena-despite-vulgar-world-baseball-classic-rant"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">OutKick</span>
            <span className="news-video-link-title">Cal Raleigh Claims 'No Beef' With Randy Arozarena — Read on OutKick ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'gen-z-sports-betting-college-wsj',
      section: '24',
      title: `Gen Z Sports Betting Is Wrecking My Friends' Lives`,
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Eli Thompson, a freshman at North Greenville University, writes that among his football teammates and peers, online sports betting has become a dangerous rite of passage. One teammate regularly places $200 NBA wagers at 18 using his debit card. Thompson describes friends who have lost hundreds, lied to their families, pulled away from the team, and chased wins that never arrive. March Madness, with wagering pools and app promotions everywhere, will only intensify the pull.
            </p>
            <p>
              Platforms like FanDuel, PrizePicks, and BetMGM make betting frictionless. Although marketing on college campuses is prohibited and the American Gaming Association requires that most ad audiences be 21+, celebrity-endorsed ads on TikTok and Instagram create an undeniable pull. Thompson recounts a friend who lost over $500 during last year's March Madness using a family-linked card and withdrew from his friend group out of shame; another burned through $600 on a relative's account; another sold his gaming console and shoes to cover a $500 loss.
            </p>
            <p>
              Pamela Brenner-Davis of the New York Council on Problem Gambling warns that younger brains are not developed enough to handle gambling's addictive nature. "Near wins" exploit this by activating reward circuits almost like real wins, encouraging continued betting. Thompson calls for parents to discuss limits and temptation, and for high schools to teach gambling risks alongside drugs and alcohol — not just financial harm, but the damage to relationships and academic life.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Thompson, E. (2026, March 9). I'm a College Student. Gen Z Sports Betting Is Wrecking My Friends' Lives.{' '}
              <a href="https://www.wsj.com/lifestyle/sports-betting-gen-z-college-908fbada" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/lifestyle/sports-betting-gen-z-college-908fbada"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">Gen Z Sports Betting Is Wrecking My Friends' Lives — Read on WSJ ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'world-cup-continent-coming-apart',
      section: '24',
      title: 'A World Cup for a Continent "Coming Apart"',
      date: 'March 8, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              While the 2026 World Cup was originally pitched as a "United Bid" to showcase North American
              cooperation, it has devolved into three parallel tournaments marked by deep geopolitical friction and
              diverging national agendas. As the tournament approaches its midway point, the trade ties (USMCA) linking
              the three economies are teetering on expiration, mirroring the frayed relationship between the host nations.
            </p>
            <h3>Political Disunity</h3>
            <p>
              Unlike the joint lobbying effort a decade ago, the three leaders—President Donald Trump, Mexican President
              Claudia Sheinbaum, and Canadian Prime Minister Mark Carney—have rarely appeared together. Trump has
              prioritized direct meetings with FIFA President Gianni Infantino, largely sidelining his North American
              counterparts.
            </p>
            <h3>Funding Disparities</h3>
            <p>
              <strong>Canada:</strong> The federal government has provided over $300 million in direct support to host
              cities. <strong>Mexico:</strong> The government implemented tax exemptions to stimulate infrastructure
              investment. <strong>United States:</strong> Host cities are largely self-funded through sponsorships. A
              federal budget squeeze and a partial government shutdown have stalled $625 million in expected security
              reimbursements, leading some cities (like Foxborough) to withhold licenses over unpaid bills.
            </p>
            <h3>Security and Immigration</h3>
            <p>
              The U.S. has maintained strict travel bans on countries like Iran and Haiti, creating uneven access for
              international fans. Additionally, the U.S. has faced calls for boycotts due to ongoing immigration
              enforcement actions, even as host cities navigate a patchwork of drone defenses and counter-terrorism plans.
            </p>
            <h3>Human Rights Standards</h3>
            <p>
              Despite a joint pledge to uphold uniform human rights and labor standards, U.S. cities missed the August
              2025 deadline for submitting protection plans. The resulting reports are inconsistent; for example,
              Vancouver's report emphasizes LGBTQ+ rights, while Houston's makes no mention of them.
            </p>
            <h3>Existential Tensions</h3>
            <p>
              Diplomatic relations are strained by Trump's suggestions of annexing Canada and his threats of
              military action against Mexican cartels. This friction looms over the July 1 deadline for the renewal of
              the USMCA trade pact.
            </p>
            <p>
              What was envisioned as a "NAFTA World Cup" is now held together primarily by FIFA's organizational
              staff. Rather than a symbol of continental integration, the event has become a reflection of the
              "crooked arc" of North American relations and the rise of isolationist policies.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Cai, S. (2026, March 8). A World Cup for a continent that's coming apart.{' '}
              <a href="https://www.politico.com/news/2026/03/08/a-world-cup-for-a-continent-thats-coming-apart-00786283" target="_blank" rel="noopener noreferrer">POLITICO</a>.
            </p>
          </div>
          <a
            href="https://www.politico.com/news/2026/03/08/a-world-cup-for-a-continent-thats-coming-apart-00786283"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">POLITICO</span>
            <span className="news-video-link-title">A World Cup for a continent that's coming apart — Read on POLITICO ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'cadillac-f1',
      section: '24',
      title: "Cadillac's Billion-Dollar F1 Gamble",
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
          <p>Most teams buy their way into Formula 1. Cadillac is building it from the ground up.</p>
          <p>Backed by General Motors and TWG Motorsports, the new American entry paid a <strong>$450 million "dilution fee"</strong> to existing teams just to be allowed on the grid — before a single car was built. Total investment: over <strong>$1 billion</strong>. They received <strong>143,000 job applications</strong> for <strong>595 open roles</strong> — a 0.4% acceptance rate tighter than most Ivy League schools.</p>
          <blockquote>"We started from a blank sheet of paper." — Team Principal Graeme Lowdon</blockquote>
          <p>Unlike every other constructor, Cadillac has no legacy infrastructure to lean on. Their operation is triangulated across three locations:</p>
          <ul>
            <li><strong>Silverstone, England</strong> — aerodynamic design</li>
            <li><strong>Charlotte, NC</strong> — F1 simulator on the GM campus</li>
            <li><strong>Indianapolis, IN</strong> — headquarters and future manufacturing</li>
          </ul>
          <p>To maximize competitiveness at debut, they bypassed rookies entirely — signing F1 veterans <strong>Valtteri Bottas</strong> (Finland) and <strong>Sergio Pérez</strong> (Mexico) for the cockpit.</p>
          <p>The power unit situation is temporary by design. Cadillac runs <strong>Ferrari engines through 2029</strong>, at which point General Motors will supply a proprietary power unit built in Indianapolis — the first American-built F1 engine in decades.</p>
          <p>The team made its debut on <strong>March 8, 2026</strong> at the Australian Grand Prix in Melbourne.</p>
          <p><a href="https://www.wsj.com/sports/cadillac-f1-team-3b85cdbb" target="_blank" rel="noopener noreferrer">Read the full story at WSJ ↗</a></p>
        </div>
          <a
            href="https://www.wsj.com/sports/cadillac-f1-team-3b85cdbb"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">Cadillac's Billion-Dollar Sprint to Build an F1 Team From Scratch — Read on WSJ ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'lou-holtz',
      section: '24',
      title: '',
      date: 'March 6, 2026',
      body: () => (
        <div className="news-article-body">
          <div className="news-tweet-card">
            <div className="news-tweet-header">
              <div className="news-tweet-avatar">🏛️</div>
              <div className="news-tweet-account">
                <span className="news-tweet-name">The White House</span>
                <span className="news-tweet-handle">@WhiteHouse</span>
              </div>
            </div>
            <p className="news-tweet-text">
              "Lou was an absolute WINNER both on and off the field. Not only did he spearhead Notre Dame's HISTORIC undefeated season in 1988…he raised Millions upon Millions for struggling student-athletes, and the AMAZING University of Notre Dame (Go Irish!)…Rest in Peace, Lou. Because of Patriots like you, we have made America GREAT AGAIN!" – PRESIDENT DONALD J. TRUMP 🇺🇸
            </p>
            <div className="news-tweet-rip">
              <span className="news-tweet-rip-label">Rest in Peace</span>
              <span className="news-tweet-rip-name">LOU HOLTZ</span>
              <span className="news-tweet-rip-years">1937 – 2026</span>
            </div>
            <a
              href="https://x.com/whitehouse/status/2029984849986211961"
              target="_blank"
              rel="noopener noreferrer"
              className="news-tweet-link"
            >
              View on X ↗
            </a>
          </div>
        </div>
      ),
    },
    {
      slug: 'wbc-japan',
      section: '24',
      title: '',
      date: 'March 2026',
      body: () => (
        <div className="news-video-wrap">
          <iframe
            src="https://www.youtube.com/embed/jFgDxEntpJk?si=vTyLHxZed3Vr5kno"
            title="WBC Japan"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ),
    },
    {
      slug: 'sports',
      section: '24',
      title: 'The "Alysa Liu Effect": How an Olympic Gala Created a Global #1 Hit',
      date: 'March 2026',
      body: () => (
        <>
          <div className="news-article-body">
          <p>
            Olympic figure skater Alysa Liu just proved the power of a viral cultural moment. Her closing gala
            performance at the 2026 Winter Olympics sent the song "Stateside" by PinkPantheress and Zara Larsson
            to #1 on Spotify's Global Daily Chart.
          </p>
          <h3>Why It's Trending</h3>
          <ul>
            <li><strong>A Historic Win:</strong> Liu is the first American woman of Asian descent to win Olympic gold in figure skating.</li>
            <li><strong>Viral Performance:</strong> Her routine, set to haunting drum and bass rhythms, went viral across TikTok, Instagram, and YouTube within hours.</li>
            <li><strong>Chart-Topping Success:</strong> "Stateside" is now only the fourth all-female collaboration to ever reach #1 on Spotify's Global Chart.</li>
            <li><strong>Authentic Connection:</strong> In an era of paid promotion, this hit reached the top through an authentic moment of technical precision and storytelling.</li>
          </ul>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Abraham, H. How Figure Skater Alysa Liu Made PinkPantheress And Zara Larsson's "Stateside" A No. 1 Hit.{' '}
            <a href="https://www.billboard.com/music/chart-beat/alysa-liu-olympics-pinkpantheress-stateside-trending-up-1236186611/" target="_blank" rel="noopener noreferrer">Billboard</a>.
          </p>
        </div>
          <a
            href="https://www.youtube.com/watch?v=ThU6LK8xEdE"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">YouTube</span>
            <span className="news-video-link-title">Alysa Liu Olympic Gala Performance — Watch on YouTube ↗</span>
          </a>
        </>
      ),
    },
  ],

  stories: [
    {
      slug: 'china-shuffle-jpmorgan-poaches-goldman-zhang',
      section: "Holly's Stories",
      title: 'The China Shuffle: JPMorgan Poaches Goldman\'s Rainmaker',
      date: 'March 17, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> JPMorgan snatches a top Goldman Sachs veteran to co-lead its China investment banking arm, signaling a high-stakes bet on a regional dealmaking rebound.
            </p>
            <p>
              The chess board of high-finance in Asia just saw its biggest move of the year. JPMorgan Chase & Co. has successfully poached Lianjia "LJ" Zhang from arch-rival Goldman Sachs to co-head its China investment banking division. As global banks navigate a cooling Chinese economy and shifting geopolitical sands, this "star hire" move proves that the battle for the most connected dealmakers—those who can bridge the gap between Beijing's corridors of power and Wall Street's capital—is more intense than ever.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Poach:</strong> Lianjia Zhang, a 17-year Goldman Sachs veteran and former Co-Head of China Investment Banking, jumps ship to JPMorgan as a Managing Director and Co-Head of China IB.</li>
              <li><strong>The Structure:</strong> Zhang will share the throne with Houston Huang, creating a "power duo" aimed at capturing a larger slice of the IPO and M&A market in the world's second-largest economy.</li>
              <li><strong>The Strategic Vacuum:</strong> The move comes as JPMorgan seeks to fill the void left by Murli Maiya, who transitioned to a CEO role for South and Southeast Asia, necessitating a heavy-hitter to maintain China dominance.</li>
              <li><strong>The Rivalry:</strong> This lateral move at the MD level is a direct blow to Goldman Sachs, which has been aggressively defending its market share in the region amidst a broader slowdown in Chinese offshore listings.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This isn't just a personnel change; it's a strategic signal that Wall Street isn't retreating from China—it's retooling. By hiring a veteran with nearly two decades of institutional memory from its primary competitor, JPMorgan is betting that the current "deal desert" in China is temporary. They are positioning themselves to be the "first money in" when the regulatory environment thaws, ensuring they have the deepest relationships on the ground to lead the next wave of massive Chinese tech and green-energy privatizations.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              JPMorgan Hires Goldman's Zhang to Co-Head China IB Business.{' '}
              <a href="https://www.bloomberg.com/news/articles/2026-03-17/jpmorgan-hires-goldman-s-zhang-to-co-head-china-ib-business" target="_blank" rel="noopener noreferrer">Bloomberg</a>, March 17, 2026.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/articles/2026-03-17/jpmorgan-hires-goldman-s-zhang-to-co-head-china-ib-business"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg</span>
            <span className="news-video-link-title">JPMorgan Hires Goldman's Zhang to Co-Head China IB Business ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'yen-line-in-sand-katayama-bold-action',
      section: "Holly's Stories",
      title: 'The Yen\'s Line in the Sand: Katayama Signals "Bold Action"',
      date: 'March 17, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              <strong>The Headline:</strong> Japan's Finance Minister issues a "free hand" warning to speculators as the Yen defies fundamentals and slides toward the danger zone.
            </p>
            <p>
              Tokyo is officially on high alert. Finance Minister Satsuki Katayama has broken the silence, characterizing recent Yen volatility as "clearly not in line with fundamentals" and driven by speculative fever. With the currency flirting with the psychologically bruising 160 level against the dollar—a threshold that previously triggered record-breaking interventions—Katayama's rhetoric marks a shift from passive observation to active threat, signaling that the Ministry of Finance is ready to pull the trigger on a massive liquidity soak.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Warning:</strong> Katayama explicitly stated Japan has a "free hand" to take bold action, a phrase designed to keep markets guessing on the timing and scale of potential direct intervention.</li>
              <li><strong>The Disconnect:</strong> Despite the Bank of Japan recently raising its policy rate to a 30-year high of 0.75%, the Yen has continued to weaken, exposing a deep skepticism among traders regarding Japan's "hawkish" transition.</li>
              <li><strong>The Speculator Moat:</strong> Policymakers are shifting the blame from economic health to "speculative movements," aligning with a joint Japan-U.S. statement to justify potential market entrance to stabilize the exchange rate.</li>
              <li><strong>The Volatility Spike:</strong> Options markets are already reacting; the cost of one-month USD/JPY options is surging as derivative traders hedge against the "sudden and sharp rallies" historically caused by MoF intervention.</li>
            </ul>
            <p>
              <strong>Why it matters:</strong> This is a game of chicken between the world's third-largest economy and global macro funds. If Katayama fails to back up her "free hand" rhetoric with real capital, she risks losing credibility and inviting a further Yen collapse that would drive up import costs and crush Japanese consumer sentiment. For global markets, this is the ultimate stress test of the "carry trade": even as interest rates rise in Japan, the gravity of the U.S. dollar remains the dominant force, forcing Tokyo into a costly and potentially futile defense of its currency.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Japan Finance Minister on Yen Weakness, Energy Outlook for 2026.{' '}
              <a href="https://www.bloomberg.com/news/articles/2026-03-17/japan-s-katayama-says-forex-moves-not-in-line-with-fundamentals" target="_blank" rel="noopener noreferrer">Bloomberg Daybreak: Asia</a>, March 17, 2026.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/articles/2026-03-17/japan-s-katayama-says-forex-moves-not-in-line-with-fundamentals"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg</span>
            <span className="news-video-link-title">Japan's Katayama Says Forex Moves Not in Line With Fundamentals ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'bytedance-blackwell-malaysia-shadow-cloud-wsj',
      section: "Holly's Stories",
      title: "ByteDance's $2.5B Blackwell End-Run",
      date: 'March 12, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              TikTok's parent company isn't letting Washington's chip embargo slow its march toward AI supremacy. By leveraging a Singapore-based middleman and Malaysian data centers, ByteDance is assembling a staggering 36,000 Nvidia B200 chips — the very hardware the U.S. government has explicitly barred from Chinese soil. It's a masterclass in regulatory arbitrage: ByteDance gets the world's most powerful "forbidden" silicon, while Nvidia maintains "compliance" by selling to a third-party partner outside the restricted zone.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The Hardware Flex:</strong> ByteDance is tapping 500 Nvidia Blackwell systems via Aolani Cloud. At a projected cost of $2.5 billion, this represents one of the largest concentrated clusters of AI power in Southeast Asia.</li>
              <li><strong>The "Middleman" Model:</strong> Aolani Cloud, a Cayman Islands-headquartered entity, acts as a "Tier-1 Nvidia partner," granting priority access to chips ByteDance cannot legally buy directly, which are then leased back to the Chinese giant.</li>
              <li><strong>The Global Pivot:</strong> To justify the spend, ByteDance is scaling its non-Chinese AI suite (Seedance for video, Gauth for education), aiming to beat OpenAI and Google on their own turf.</li>
              <li><strong>The Regulatory Loophole:</strong> Nvidia's stance: export rules govern where the chips sit, not who runs code on them. Servers in Malaysia pass "compliance" checks.</li>
              <li><strong>The Talent Surge:</strong> ByteDance is hiring for over 100 AI-specific roles in Seattle and San Jose to staff foundational research teams.</li>
            </ul>
            <p>
              This move exposes the "leaky bucket" reality of U.S. tech sanctions. While the Commerce Department focuses on the Chinese border, ByteDance is moving the playground to Malaysia, South Korea, and Australia. By the time regulators close the "leasing" loophole, ByteDance may have already trained its next-generation models on Blackwell architecture. For investors, the AI arms race is no longer about geography — it's about who can navigate the most complex corporate structures to keep the GPUs humming.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Emont, J., & Lin, L. (2026, March 12). China's ByteDance Gets Access to Top Nvidia AI Chips.{' '}
              <a href="https://www.wsj.com/tech/chinas-bytedance-gets-access-to-top-nvidia-ai-chips-d68bce3a?mod=hp_lead_pos3" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/tech/chinas-bytedance-gets-access-to-top-nvidia-ai-chips-d68bce3a?mod=hp_lead_pos3"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">China's ByteDance Gets Access to Top Nvidia AI Chips ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'china-openclaw-ai-ban-state-offices',
      section: "Holly's Stories",
      title: 'China Pulls the Plug on "OpenClaw" AI in State Offices',
      date: 'March 10, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Just as "OpenClaw" (the autonomous AI agent formerly known as Clawdbot) reached a fever pitch in China, the government is stepping in to cool the frenzy. Authorities have moved swiftly to restrict state-run enterprises, government agencies, and the nation's largest banks from installing the software, fearing the tech's "agentic" nature poses a direct threat to national security.
            </p>
            <h3>The Tactical Breakdown</h3>
            <ul>
              <li><strong>The "Lethal Trifecta":</strong> Cybersecurity experts are warning that OpenClaw is uniquely risky because it requires broad access to private data, can communicate externally, and is constantly exposed to untrusted content.</li>
              <li><strong>The Ban:</strong> Targeted notices have gone out to state-owned banks and military families, demanding the removal of OpenClaw from both office computers and personal phones using official networks.</li>
              <li><strong>Market Impact:</strong> The news sent shockwaves through the tech sector. MiniMax Group Inc. — which saw its valuation rocket to $49 billion (surpassing Baidu) on the back of its "MaxClaw" integration — erased its daily gains immediately following the report.</li>
              <li><strong>The "Rogue" Risk:</strong> Beyond state secrets, individual users have reported the AI "going rogue," including instances of the agent spamming hundreds of messages once granted access to iMessage.</li>
            </ul>
            <p>
              President Xi Jinping has long viewed data as a core pillar of national security. While Chinese tech giants like Tencent and Alibaba are racing to capitalize on AI agents that can book flights and manage inboxes, the ruling Communist Party is wary of any technology that amasses massive datasets outside of direct state oversight. For the "agentic AI" era to survive in China, it's going to have to play by Beijing's very strict rules.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Bloomberg News. (2026, March 10). China Moves to Curb OpenClaw AI Use at Banks, State Agencies.{' '}
              <a href="https://www.bloomberg.com/news/articles/2026-03-09/trump-family-s-defense-investments-grow-with-a-new-drone-deal" target="_blank" rel="noopener noreferrer">Bloomberg</a>.
            </p>
          </div>
          <a
            href="https://www.bloomberg.com/news/articles/2026-03-09/trump-family-s-defense-investments-grow-with-a-new-drone-deal"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Bloomberg</span>
            <span className="news-video-link-title">China Moves to Curb OpenClaw AI Use at Banks, State Agencies ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'trump-florsheim-shoes-gifts',
      section: "Holly's Stories",
      title: `Trump's Florsheim Dress Shoe Obsession`,
      date: 'March 9, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              The hottest MAGA status symbol right now is a pair of{' '}
              <a href="https://www.florsheim.com/shop/style/13384-001.html" target="_blank" rel="noopener noreferrer">Florsheim oxfords</a>. President Trump has been gifting the American brand's dress shoes — many priced around $145 — to cabinet members, advisers, lawmakers and VIPs. "Did you get the shoes?" he asks at cabinet meetings. Some recipients have been fitted on the spot in the Oval Office. During a January lunch, Trump pivoted mid-conversation to praise his "incredible" new shoes and handed Tucker Carlson a pair of brown wingtips.
            </p>
            <p>
              "All the boys have them," said a White House official. Another joked that everyone's afraid not to wear them. Vice President JD Vance, Secretary of State Marco Rubio, Defense Secretary Pete Hegseth, Commerce Secretary Howard Lutnick, Transportation Secretary Sean Duffy, Treasury Secretary Scott Bessent, Sean Hannity and Lindsey Graham are among the recipients. One cabinet secretary reportedly grumbled about shelving his Louis Vuittons.
            </p>
            <p>
              Trump, 79, switched to Florsheim late last year for comfort after long days. He pays for the shoes himself, the White House said. He sizes people up in person, orders pairs, and sends signed brown boxes a week later. During a December Oval Office meeting, Trump looked at Rubio and Vance's feet and declared, "Marco, JD, you guys have s—y shoes," then pulled out a catalog. Rubio, who wore $135 Florsheim boots during his 2016 run (sparking "bootgate" speculation), got a pair. Florsheim, founded in Chicago in 1892, outfitted soldiers in both world wars; Harry Truman and Michael Jackson wore the brand.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Leary, A. (2026, March 9). Trump is obsessed with these $145 shoes—and won't let anyone leave without a pair.{' '}
              <a href="https://www.wsj.com/style/fashion/trump-florsheim-shoes-tucker-carlson-jd-vance-bessent-448567ab" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/style/fashion/trump-florsheim-shoes-tucker-carlson-jd-vance-bessent-448567ab"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">Trump is obsessed with these $145 shoes—and won't let anyone leave without a pair — Read on WSJ ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'nepal-rapper-politician-victory',
      section: "Holly's Stories",
      title: "Rapper-Turned-Politician Sweeps to Victory in Nepal",
      date: 'March 8, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Balendra Shah, a 35-year-old former rapper and Mayor of Kathmandu, has achieved a historic landslide
              victory in Nepal's first general election since the youth-led revolution of 2025. Leading the centrist
              Rastriya Swatantra Party (RSP), Shah has effectively dismantled the country's traditional political
              "old guard."
            </p>
            <h3>Decisive Mandate</h3>
            <p>
              As of March 8, 2026, the RSP has won 122 of the 165 directly elected seats, unseating established giants
              like former Prime Minister KP Sharma Oli. In contrast, the traditional Nepali Congress and Communist
              parties won only 17 and 8 seats, respectively.
            </p>
            <h3>Youth-Led Revolution</h3>
            <p>
              The election follows a violent uprising in September 2025—triggered by a social media ban—that resulted
              in 77 deaths and the resignation of the previous government. Shah capitalized on the frustrations of a
              nation where the median age is just 25.
            </p>
            <h3>Economic and Social Platform</h3>
            <p>
              The RSP campaigned on an anti-corruption and pro-governance platform. Specifically, the party pledged
              to create 1.2 million jobs to address a 21% youth unemployment rate that has historically forced
              millions of Nepalis to seek work abroad.
            </p>
            <h3>Geopolitical Shift</h3>
            <p>
              Shah intends to move Nepal away from being a "buffer state" between China and India, instead
              positioning it as a "bridge" to promote trilateral economic connectivity.
            </p>
            <h3>Historic Leadership</h3>
            <p>
              Upon taking office, Shah will become the youngest elected South Asian leader since Benazir Bhutto in 1988.
            </p>
            <p>
              The victory represents a fundamental shift in Nepali politics, moving away from decades of dominance by
              Maoist, Communist, and Monarchist factions toward a Gen Z-driven focus on reform and economic stagnation.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Schipani, A. (2026, March 7). Rapper-turned-politician sweeps to victory in Nepal.{' '}
              <a href="https://www.ft.com/content/63a03711-d2b6-488f-a01f-0335863692fd" target="_blank" rel="noopener noreferrer">Financial Times</a>.
            </p>
          </div>
          <a
            href="https://www.ft.com/content/63a03711-d2b6-488f-a01f-0335863692fd"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Financial Times</span>
            <span className="news-video-link-title">Rapper-turned-politician sweeps to victory in Nepal — Read on FT ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'african-conscripts-ukraine',
      section: "Holly's Stories",
      title: "African Conscripts in the Ukraine War",
      date: 'March 7, 2026',
      body: () => (
        <>
          <div className="news-article-body">
          <p>
            Russia is increasingly filling its frontline gaps with thousands of African nationals — many
            lured by human trafficking syndicates under the guise of civilian jobs. According to Ukrainian
            intelligence, at least <strong>1,780 Africans from 36 countries</strong> are currently
            enrolled in the Russian army, though local agencies suggest the real number is far higher.
            Kenya alone estimates over 1,000 of its citizens have been involved.
          </p>
          <h3>The Trafficking Trap</h3>
          <p>
            Agencies like "Global Face Human Resources" promised young men <strong>$10,000 sign-on
            bonuses</strong> and civilian jobs. Upon arrival in St. Petersburg, recruits were forced to
            sign military contracts in Russian — or pay exit fees as high as <strong>$31,000</strong>.
          </p>
          <p>
            Recruits often receive only <strong>one week of training</strong> before being sent to active
            combat zones in eastern Ukraine. Estimates suggest <strong>42% of foreign fighters are killed
            within four months</strong>. Ghana's Foreign Minister Samuel Okudzeto Ablakwa reports at
            least 55 confirmed Ghanaian deaths.
          </p>
          <h3>The Geopolitical Fallout</h3>
          <p>
            With desertion rates at record highs, the Kremlin is using foreign conscripts to avoid a
            politically unpopular domestic general mobilization. Governments in Kenya and Ghana are now
            cracking down on unlicensed agencies. Ghana is lobbying for an international coalition to
            dismantle this trafficking pipeline. Russia has reportedly blacklisted 36 "friendly" countries
            from further recruitment to avoid a total collapse of its image across the continent.
          </p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            The Economist. (2026, March 5). Thousands of Africans are fighting for Russia in Ukraine.{' '}
            <a href="https://www.economist.com/middle-east-and-africa/2026/03/05/thousands-of-africans-are-fighting-for-russia-in-ukraine" target="_blank" rel="noopener noreferrer">The Economist</a>.
            Reported from Accra and Nairobi.
          </p>
        </div>
          <a
            href="https://www.economist.com/middle-east-and-africa/2026/03/05/thousands-of-africans-are-fighting-for-russia-in-ukraine"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Economist</span>
            <span className="news-video-link-title">Thousands of Africans are fighting for Russia in Ukraine — Read on The Economist ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'china-fbi-breach',
      section: "Holly's Stories",
      title: 'China Suspected in Major Breach of FBI Surveillance Network',
      date: 'March 6, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              U.S. investigators are probing a significant cyber intrusion into an internal FBI computer
              network, with evidence pointing toward hackers affiliated with the Chinese government. The
              breach targeted an unclassified system containing sensitive data tied to domestic surveillance
              orders — including call logs, IP addresses, and routing information for criminal suspects.
            </p>
            <p>
              The contents of communications were reportedly not compromised. Still, the hack lands as a
              serious intelligence exposure, coming on the heels of the "Salt Typhoon" breach of 2024, which
              compromised the personal data of senior officials including President Trump.
            </p>
            <p>
              The FBI confirmed it has addressed "suspicious activities" on its networks but declined to
              provide further specifics as the investigation continues. No attribution has been made
              officially.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Volz, D. (2026, March 6). China Suspected in Breach of FBI Surveillance Network.{' '}
              <a href="https://www.wsj.com/politics/policy/china-suspected-in-breach-of-fbi-surveillance-network-533pm-et" target="_blank" rel="noopener noreferrer">The Wall Street Journal</a>.
            </p>
          </div>
          <a
            href="https://www.wsj.com/politics/policy/china-suspected-in-breach-of-fbi-surveillance-network-533pm-et"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Wall Street Journal</span>
            <span className="news-video-link-title">China Suspected in Breach of FBI Surveillance Network — Read on WSJ ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'boeing-china-500-jets',
      section: "Holly's Stories",
      title: 'Trump Meets with Xi to Secure Massive 500-Jet Boeing Order',
      date: 'March 6, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              Boeing shares surged 4.1% on Friday following reports that China is nearing a massive order
              for 500 aircraft. The potential deal follows a high-level discussion between President Trump
              and Chinese President Xi Jinping, where aerospace trade was a primary focus.
            </p>
            <p>
              China was historically Boeing's largest customer, accounting for roughly 20% of deliveries
              for a decade — until relations soured and orders stalled after 2019. The company has not
              received a 737 order from China since. A 500-jet commitment would significantly bolster
              Boeing's existing 6,700-plane backlog as the company works to scale production back to
              pre-2018 levels.
            </p>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              Root, A. (2026, March 6). Boeing Stock Jumps. It's China, Again.{' '}
              <a href="https://www.barrons.com/articles/boeing-stock-jumps-its-china-again-abccfbd3" target="_blank" rel="noopener noreferrer">Barron's</a>.
            </p>
          </div>
          <a
            href="https://www.barrons.com/articles/boeing-stock-jumps-its-china-again-abccfbd3"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">Barron's</span>
            <span className="news-video-link-title">Boeing Stock Jumps. It's China, Again. — Read on Barron's ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'china-bube-railway',
      section: "Holly's Stories",
      title: "China's First EU Railway Finally Runs",
      date: 'March 5, 2026',
      body: () => (
        <>
          <div className="news-article-body">
            <p>
              A decade after its conception, the Budapest-Belgrade (BuBe) railway officially opened its final
              stretch on February 27, 2026. While the project has faced its share of technical and political
              hurdles, its launch marks a historic turning point: the first time Chinese high-speed rail
              technology and standards have been fully operationalized within the European Union.
            </p>
            <h3>Key Highlights of the "BuBe" Opening</h3>
            <ul>
              <li><strong>The "First" Factor:</strong> This is the flagship entry of China's Belt and Road Initiative (BRI) into the EU, proving that Chinese engineering can meet the bloc's stringent Technical Specifications for Interoperability (TSI).</li>
              <li><strong>Slashing Travel Times:</strong> The 350km link between the Hungarian and Serbian capitals is now a high-speed reality, cutting a once eight-hour journey down to approximately three and a half hours.</li>
              <li><strong>Economic Anchor:</strong> Beyond transit, the line serves as a permanent physical anchor for the billions in Chinese investment currently flowing into Central Europe's electric vehicle and battery sectors.</li>
            </ul>
            <p className="news-article-meta" style={{ marginTop: '2rem' }}>
              China's first railway project in the EU is open at last. (2026, March 5).{' '}
              <a href="https://www.economist.com/china/2026/03/05/chinas-first-railway-project-in-the-eu-is-open-at-last" target="_blank" rel="noopener noreferrer">The Economist</a>.
            </p>
          </div>
          <a
            href="https://www.economist.com/china/2026/03/05/chinas-first-railway-project-in-the-eu-is-open-at-last"
            target="_blank"
            rel="noopener noreferrer"
            className="news-video-link"
          >
            <span className="news-video-link-label">The Economist</span>
            <span className="news-video-link-title">Strategic Milestone: The Budapest-Belgrade Railway Debut — Read on The Economist ↗</span>
          </a>
        </>
      ),
    },
    {
      slug: 'spain-trade',
      section: "Holly's Stories",
      title: 'Trump Threatens Full Trade Embargo on Spain Following Base Access Dispute',
      date: 'March 3, 2026',
      body: () => (
        <div className="news-article-body">
          <p>President Trump has announced a plan to terminate all trade with Spain, following the Spanish government's refusal to allow U.S. forces to use joint military bases for strikes against Iran. This move signifies a major escalation in diplomatic tensions and highlights a growing rift within the NATO alliance.</p>
          <h3>Key Conflict Points</h3>
          <ul>
            <li><strong>Base Access:</strong> Spain's Foreign Minister stated that bases in southern Spain cannot be used for any military actions not authorized by the UN Charter.</li>
            <li><strong>NATO Spending:</strong> The President criticized Spain as the only member resisting a 5% defense spending target.</li>
            <li><strong>Legal Battle:</strong> While the Supreme Court recently limited unilateral tariffs, the administration argues it still has the authority to implement full-scale trade embargoes.</li>
          </ul>
          <p>In response to these threats, Spanish officials have defended their role in NATO. They emphasized that any trade changes must respect international law and existing European Union agreements.</p>
          <p className="news-article-meta" style={{ marginTop: '2rem' }}>
            Arasu, S. and Naishadham, S. (2026, March 3). Trump threatens to cut off trade with Spain after it disallowed US use of joint bases in Iran war.{' '}
            <a href="https://apnews.com/article/trump-spain-iran-trade-defense-nato-spending-43e0f13e7b1c7e6ebcc4b558474aacdc" target="_blank" rel="noopener noreferrer">Associated Press (AP News)</a>.
          </p>
        </div>
      ),
    },
  ],
};

const SECTION_LABELS = {
  labor:   'In Labor',
  energy:  'No Energy',
  meds:    'Rx',
  stories: "Holly's Stories",
  bored:   'Above Bored',
  fish:    'Fish',
  '24':    '24',
};

const SECTION_BADGES = {
  labor:   { label: 'In Labor',        color: '#7a9ab0' },
  energy:  { label: 'No Energy',       color: '#a88c42' },
  meds:    { label: 'Rx',              color: '#6a9a7c' },
  stories: { label: "Holly's Stories", color: '#9a8060' },
  bored:   { label: 'Above Bored',     color: '#7a8898' },
  fish:    { label: 'Fish',            color: '#4a8888' },
  '24':    { label: '24',              color: '#a05050' },
};

// ─── Article card component ────────────────────────────────────────────────────
function ArticleCard({ article, sectionKey, single = false }) {
  const permalink = `/paper/${sectionKey}/${article.slug}`;
  const badge = SECTION_BADGES[sectionKey];
  return (
    <div className="news-article">
      <div className="news-article-top-row">
        {badge && (
          <span
            className="news-section-badge"
            style={{ '--badge-color': badge.color }}
          >
            {badge.label}
          </span>
        )}
        {!single && (
          <Link to={permalink} className="news-permalink" title="Permalink">¶</Link>
        )}
      </div>
      {article.title && <h2 className="news-article-title">{article.title}</h2>}
      <p className="news-article-meta">{article.date}</p>
      {article.body()}
      <ArticleComments slug={article.slug} sectionKey={sectionKey} />
    </div>
  );
}

// ─── NewsPage ──────────────────────────────────────────────────────────────────
function NewsPage({ onNavigateToJobs }) {
  const { section, article: articleSlug } = useParams();
  const navigate = useNavigate();
  const currentSection = section || 'main';

  const handleNavClick = (s) => {
    navigate(s === 'main' ? '/paper' : `/paper/${s}`);
  };

  const renderPlaceholder = (title) => (
    <div className="news-placeholder">
      <h2>{title}</h2>
      <p>Coming soon.</p>
    </div>
  );

  // Single article view
  const sectionArticles = ARTICLES[currentSection] || [];
  const singleArticle = articleSlug
    ? sectionArticles.find(a => a.slug === articleSlug)
    : null;

  return (
    <div className="news-page">
      <PaperHeader onNavigateToJobs={onNavigateToJobs} section={currentSection} />
      <header className="news-header">
        <h1>The Paper</h1>
        <nav className="news-nav">
          <button type="button" className={currentSection === 'labor'   ? 'active' : ''} onClick={() => handleNavClick('labor')}>In Labor</button>
          <button type="button" className={currentSection === 'energy'  ? 'active' : ''} onClick={() => handleNavClick('energy')}>No Energy</button>
          <button type="button" className={currentSection === 'meds'    ? 'active' : ''} onClick={() => handleNavClick('meds')}>Rx</button>
          <button type="button" className={currentSection === 'stories' ? 'active' : ''} onClick={() => handleNavClick('stories')}>Holly's Stories</button>
          <button type="button" className={currentSection === 'bored'   ? 'active' : ''} onClick={() => handleNavClick('bored')}>Above Bored</button>
          <button type="button" className={currentSection === 'fish'    ? 'active' : ''} onClick={() => handleNavClick('fish')}>Fish</button>
          <button type="button" className={currentSection === '24'      ? 'active' : ''} onClick={() => handleNavClick('24')}>24</button>
        </nav>
      </header>

      <main className="news-content">
        {currentSection === 'main' && (
          <div className="news-featured">
            <h2>Another day Another dollhair</h2>
            <p>back breaking news</p>
          </div>
        )}

        {/* Single article view */}
        {singleArticle && (
          <>
            <Link to={`/paper/${currentSection}`} className="news-back-link">
              ← {SECTION_LABELS[currentSection] || currentSection}
            </Link>
            <ArticleCard article={singleArticle} sectionKey={currentSection} single />
          </>
        )}

        {/* Article not found */}
        {articleSlug && !singleArticle && (
          <div className="news-placeholder">
            <h2>Article not found</h2>
            <p><Link to={`/paper/${currentSection}`}>← Back</Link></p>
          </div>
        )}

        {/* Section view — all articles */}
        {!articleSlug && sectionArticles.length > 0 && (
          <>
            {sectionArticles.map((article, i) => (
              <React.Fragment key={article.slug}>
                {i > 0 && (
                  <div className="news-article-divider">
                    <span>Earlier</span>
                  </div>
                )}
                <ArticleCard article={article} sectionKey={currentSection} />
              </React.Fragment>
            ))}
          </>
        )}

        {/* Placeholder sections */}
        {!articleSlug && currentSection === 'meds'   && sectionArticles.length === 0 && renderPlaceholder('Rx')}
        {!articleSlug && currentSection === 'labor'  && sectionArticles.length === 0 && renderPlaceholder('In Labor')}
        {!articleSlug && currentSection === 'energy' && sectionArticles.length === 0 && renderPlaceholder('No Energy')}
        {!articleSlug && currentSection === '24' && sectionArticles.length === 0 && (
          <div className="news-article">
            <p className="news-article-section">24</p>
            <h2 className="news-article-title">Sports by 2thirty</h2>
            <div className="news-article-body"><p>Coming soon.</p></div>
          </div>
        )}
      </main>
    </div>
  );
}

export default NewsPage;
